import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { Card, CardContent } from './ui/Card';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Button } from './ui/Button';
import { Switch } from './ui/Switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/Tooltip';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell, Legend, Area, AreaChart, Sector } from 'recharts';
import { toPng, toCanvas } from 'html-to-image';
import { jsPDF } from 'jspdf';
// --- Helpers ---
const fmtCurrency = (n: number) => n.toLocaleString(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const fmtNum = (n: number, d: number = 0) => n.toLocaleString(undefined, {
  maximumFractionDigits: d
});
// Payment schedule helper — returns per-cohort payment for a given relative month r (1-indexed)
function cohortPayment(mode: 'equal' | 'upfront' | 'split', r: number, ltvTotal: number, ltvMonths: number, upfrontPct: number, remainderMonths: number) {
  if (mode === 'equal') {
    return r >= 1 && r <= ltvMonths ? ltvTotal / ltvMonths : 0;
  }
  if (mode === 'upfront') {
    return r === 1 ? ltvTotal : 0;
  }
  // split
  const upfront = r === 1 ? ltvTotal * upfrontPct : 0;
  const remEach = remainderMonths > 0 ? ltvTotal * (1 - upfrontPct) / remainderMonths : 0;
  const remPay = r >= 1 && r <= remainderMonths ? remEach : 0;
  return upfront + remPay;
}
// Industry benchmark constants
const INDUSTRY_BENCHMARKS = {
  showRate: 0.8,
  closeRate: 0.2,
  costPerBookedCall: 250,
  fulfillmentPct: 0.25,
  ltvMonths: 3,
  upfrontPct: 0.5 // 50% upfront for split payment model
};
export function RevenueCalculator() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Core inputs
  const [salesVelocity, setSalesVelocity] = useState(40); // clients sold per month
  const [ltvTotal, setLtvTotal] = useState(6000); // total LTV dollars
  const [ltvMonths, setLtvMonths] = useState(6); // months to collect LTV (equal payments)
  // Collection mode
  const [collectionMode, setCollectionMode] = useState<'equal' | 'upfront' | 'split'>('upfront'); // Changed default from 'equal' to 'upfront'
  const [upfrontPct, setUpfrontPct] = useState(0.3); // for split mode
  const [remainderMonths, setRemainderMonths] = useState(5); // for split mode
  // Funnel inputs
  const [showRate, setShowRate] = useState(0.6); // from booked calls to showed calls (below benchmark of 0.8)
  const [closeRate, setCloseRate] = useState(0.15); // from showed calls to clients (below benchmark of 0.2)
  const [costPerBookedCall, setCostPerBookedCall] = useState(300); // spend per booked call (above benchmark of 250)
  // Capacity inputs
  const [workdaysPerWeek, setWorkdaysPerWeek] = useState(5);
  const [weeksPerMonth, setWeeksPerMonth] = useState(4.33);
  const [callsPerCloserPerDay, setCallsPerCloserPerDay] = useState(6); // sales calls a closer can handle per day
  const [roundUpClosers, setRoundUpClosers] = useState(true);
  // Direct costs inputs
  const [directCosts, setDirectCosts] = useState([{
    id: 1,
    name: 'Staff Salaries',
    quantity: 1,
    amount: 2000,
    category: 'Other',
    costType: 'fixed' // 'fixed', 'percentage', or 'profit-percentage'
  }, {
    id: 2,
    name: 'Software Tools',
    quantity: 1,
    amount: 500,
    category: 'Other',
    costType: 'fixed'
  }, {
    id: 3,
    name: 'Materials',
    quantity: 1,
    amount: 800,
    category: 'Other',
    costType: 'fixed'
  }]);
  const [newCostName, setNewCostName] = useState('');
  const [newCostQuantity, setNewCostQuantity] = useState(1);
  const [newCostAmount, setNewCostAmount] = useState(0);
  const [newCostCategory, setNewCostCategory] = useState('Other');
  const [newCostType, setNewCostType] = useState('fixed');
  const [editingCostId, setEditingCostId] = useState<number | null>(null);
  const [editCostName, setEditCostName] = useState('');
  const [editCostQuantity, setEditCostQuantity] = useState(1);
  const [editCostAmount, setEditCostAmount] = useState(0);
  const [editCostCategory, setEditCostCategory] = useState('Other');
  const [editCostType, setEditCostType] = useState('fixed');
  // Comp & cost inputs
  const [commissionPct, setCommissionPct] = useState(0.1); // 10% default
  const [commissionBase, setCommissionBase] = useState<'ltv' | 'collected'>('ltv');
  const [fulfillmentPct, setFulfillmentPct] = useState(0.3); // % of revenue as variable COGS
  // Calculate total direct costs and sales & marketing costs
  const {
    totalDirectCosts,
    salesAndMarketingCosts
  } = useMemo(() => {
    let totalCosts = 0;
    let salesMarketingCosts = 0;
    directCosts.forEach(cost => {
      let costValue;
      if (cost.costType === 'fixed') {
        costValue = cost.quantity * cost.amount;
      } else {
        // For percentage-based costs, we'll calculate this in the derived section
        // For now, just use 0 to avoid circular dependency
        costValue = 0;
      }
      totalCosts += costValue;
      if (cost.category === 'Sales and Marketing') {
        salesMarketingCosts += costValue;
      }
    });
    return {
      totalDirectCosts: totalCosts,
      salesAndMarketingCosts: salesMarketingCosts
    };
  }, [directCosts]); // Remove derived dependency to break circular reference
  // Add a new direct cost
  const addDirectCost = () => {
    if (newCostName.trim() && (newCostType === 'fixed' ? newCostAmount > 0 : newCostAmount >= 0) && newCostQuantity > 0) {
      setDirectCosts([...directCosts, {
        id: Date.now(),
        name: newCostName.trim(),
        quantity: newCostQuantity,
        amount: newCostAmount,
        category: newCostCategory,
        costType: newCostType
      }]);
      setNewCostName('');
      setNewCostQuantity(1);
      setNewCostAmount(0);
      setNewCostCategory('Other');
      setNewCostType('fixed');
    }
  };
  // Remove a direct cost
  const removeDirectCost = (id: number) => {
    setDirectCosts(directCosts.filter(cost => cost.id !== id));
  };
  // Start editing a direct cost
  const startEditingCost = (cost: {
    id: number;
    name: string;
    quantity: number;
    amount: number;
    category: string;
    costType: string;
  }) => {
    setEditingCostId(cost.id);
    setEditCostName(cost.name);
    setEditCostQuantity(cost.quantity);
    setEditCostAmount(cost.amount);
    setEditCostCategory(cost.category);
    setEditCostType(cost.costType);
  };
  // Save edited direct cost
  const saveEditedCost = () => {
    if (editingCostId && editCostName.trim() && (editCostType === 'fixed' ? editCostAmount > 0 : editCostAmount >= 0) && editCostQuantity > 0) {
      setDirectCosts(directCosts.map(cost => cost.id === editingCostId ? {
        ...cost,
        name: editCostName.trim(),
        quantity: editCostQuantity,
        amount: editCostAmount,
        category: editCostCategory,
        costType: editCostType
      } : cost));
      setEditingCostId(null);
    }
  };
  // Cancel editing
  const cancelEditing = () => {
    setEditingCostId(null);
  };
  // Derived calculations
  const derived = useMemo(() => {
    // steady-state monthly revenue depends on collection mode
    let steadyStateMonthlyRevenue = 0;
    if (collectionMode === 'equal') {
      steadyStateMonthlyRevenue = salesVelocity * (ltvTotal / Math.max(1, ltvMonths));
    } else if (collectionMode === 'upfront') {
      steadyStateMonthlyRevenue = salesVelocity * ltvTotal; // all cash month 1 of each cohort
    } else {
      // split mode: upfront every month for new cohort + stacked remainder installments
      const upfront = ltvTotal * upfrontPct;
      const remEach = remainderMonths > 0 ? ltvTotal * (1 - upfrontPct) / remainderMonths : 0;
      steadyStateMonthlyRevenue = salesVelocity * (upfront + remEach * Math.max(1, remainderMonths));
    }
    // First calculate non-profit-based costs
    let actualTotalDirectCosts = 0;
    let actualSalesAndMarketingCosts = 0;
    let profitBasedCosts = []; // Store profit-based costs for later calculation
    directCosts.forEach(cost => {
      if (cost.costType === 'profit-percentage') {
        // Store profit-based costs for later calculation after we know the profit
        profitBasedCosts.push(cost);
      } else {
        let costValue;
        if (cost.costType === 'fixed') {
          costValue = cost.quantity * cost.amount;
        } else {
          // For percentage-based costs, calculate based on steady state revenue
          costValue = steadyStateMonthlyRevenue > 0 ? cost.amount / 100 * steadyStateMonthlyRevenue : 0;
        }
        actualTotalDirectCosts += costValue;
        if (cost.category === 'Sales and Marketing') {
          actualSalesAndMarketingCosts += costValue;
        }
      }
    });
    // Required booked calls & ad spend
    const effShow = Math.max(1e-6, showRate);
    const effClose = Math.max(1e-6, closeRate);
    const effectiveConversion = effShow * effClose; // deals per booked call
    const bookedCallsPerMonthNeeded = salesVelocity / effectiveConversion;
    const adSpendPerMonth = bookedCallsPerMonthNeeded * Math.max(0, costPerBookedCall);
    // Commission model
    const commissionMonthly = commissionBase === 'ltv' ? salesVelocity * commissionPct * ltvTotal : commissionPct * steadyStateMonthlyRevenue; // paid on collected cash
    // Initial calculation of operating profit (without profit-based costs)
    const initialOperatingProfitMonthly = steadyStateMonthlyRevenue - adSpendPerMonth - commissionMonthly - actualTotalDirectCosts;
    // Now calculate profit-based costs
    let profitBasedTotalCosts = 0;
    let profitBasedSalesAndMarketingCosts = 0;
    profitBasedCosts.forEach(cost => {
      // Only calculate if there's profit to take a percentage of
      const costValue = initialOperatingProfitMonthly > 0 ? cost.amount / 100 * initialOperatingProfitMonthly * cost.quantity : 0;
      profitBasedTotalCosts += costValue;
      if (cost.category === 'Sales and Marketing') {
        profitBasedSalesAndMarketingCosts += costValue;
      }
    });
    // Add profit-based costs to the totals
    actualTotalDirectCosts += profitBasedTotalCosts;
    actualSalesAndMarketingCosts += profitBasedSalesAndMarketingCosts;
    // Calculate fulfillment cost percentage based on all direct costs
    const calculatedFulfillmentPct = steadyStateMonthlyRevenue > 0 ? actualTotalDirectCosts / steadyStateMonthlyRevenue : 0;
    // True/Effective costs along the funnel
    const costPerShow = costPerBookedCall / effShow; // what a show really costs
    const costPerClientFromBooked = costPerBookedCall / effectiveConversion; // CPA implied by CPBC
    // Call volume per time bucket
    const weeksNeeded = weeksPerMonth; // per month
    const bookedCallsPerWeek = bookedCallsPerMonthNeeded / weeksNeeded;
    const bookedCallsPerDay = bookedCallsPerWeek / Math.max(1, workdaysPerWeek);
    // Closers needed
    const closersNeededRaw = bookedCallsPerDay / Math.max(1e-6, callsPerCloserPerDay);
    const closersNeeded = roundUpClosers ? Math.ceil(closersNeededRaw) : closersNeededRaw;
    const revenuePerCloser = steadyStateMonthlyRevenue / Math.max(1, closersNeeded);
    const compPerCloserMonthly = commissionMonthly / Math.max(1, closersNeeded);
    // Fulfillment variable cost and profit (now with profit-based costs included)
    const fulfillmentCostMonthly = steadyStateMonthlyRevenue * Math.max(0, calculatedFulfillmentPct);
    const operatingProfitMonthly = steadyStateMonthlyRevenue - adSpendPerMonth - commissionMonthly - fulfillmentCostMonthly;
    const operatingMarginPct = steadyStateMonthlyRevenue > 0 ? operatingProfitMonthly / steadyStateMonthlyRevenue : 0;
    // Ramp timeline (visualization). Compute generically by cohort summation
    const monthsToSteady = collectionMode === 'equal' ? Math.max(1, ltvMonths) : collectionMode === 'upfront' ? 1 : Math.max(1, remainderMonths);
    const horizon = Math.max(monthsToSteady * 2, 12);
    const data: {
      month: number;
      revenue: number;
      adSpend: number;
      commissions: number;
      directCosts: number;
      operatingProfit: number;
      operatingMargin: number;
      newClients: number;
      bookedCalls: number;
      shows: number;
    }[] = [];
    for (let t = 1; t <= horizon; t++) {
      let revenueT = 0;
      for (let cohort = 1; cohort <= t; cohort++) {
        const r = t - cohort + 1; // relative month for this cohort
        revenueT += cohortPayment(collectionMode, r, ltvTotal, Math.max(1, ltvMonths), upfrontPct, Math.max(1, remainderMonths));
      }
      revenueT *= salesVelocity;
      // Calculate metrics for this month's revenue level
      let monthCommissions = commissionBase === 'ltv' ? salesVelocity * commissionPct * ltvTotal : commissionPct * revenueT;
      // Calculate direct costs for this month (simplified, using the same percentage)
      let monthDirectCosts = revenueT * calculatedFulfillmentPct;
      // Ad spend is constant based on sales velocity
      let monthAdSpend = adSpendPerMonth;
      // Operating profit for this month
      let monthOperatingProfit = revenueT - monthAdSpend - monthCommissions - monthDirectCosts;
      // Operating margin for this month
      let monthOperatingMargin = revenueT > 0 ? monthOperatingProfit / revenueT : 0;
      data.push({
        month: t,
        revenue: revenueT,
        adSpend: monthAdSpend,
        commissions: monthCommissions,
        directCosts: monthDirectCosts,
        operatingProfit: monthOperatingProfit,
        operatingMargin: monthOperatingMargin,
        newClients: salesVelocity,
        bookedCalls: bookedCallsPerMonthNeeded,
        shows: bookedCallsPerMonthNeeded * showRate
      });
    }
    const annualRevenue = steadyStateMonthlyRevenue * 12;
    // Split payment breakdown
    const upfrontAmount = ltvTotal * upfrontPct;
    const remainderAmount = ltvTotal * (1 - upfrontPct);
    const monthlyPaymentAmount = remainderMonths > 0 ? remainderAmount / remainderMonths : 0;
    // Calculate fully loaded CAC
    const baseCAC = costPerClientFromBooked;
    const salesAndMarketingCostPerClient = salesVelocity > 0 ? actualSalesAndMarketingCosts / salesVelocity : 0;
    const commissionPerClient = salesVelocity > 0 ? commissionMonthly / salesVelocity : 0;
    const fullyLoadedCAC = baseCAC + salesAndMarketingCostPerClient + commissionPerClient;
    // Recalculate LTV:CAC ratio with fully loaded CAC
    const fullyLoadedLtvCacRatio = ltvTotal / Math.max(0.01, fullyLoadedCAC);
    // Scalability viability
    const ltvCacRatio = ltvTotal / costPerClientFromBooked;
    const cashCollectionSpeed = collectionMode === 'upfront' ? 1 : collectionMode === 'equal' ? ltvMonths : remainderMonths;
    // Calculate scalability score (0-100)
    let scalabilityScore = 0;
    // Factor 1: Operating margin (0-40 points)
    const marginScore = Math.min(40, Math.max(0, operatingMarginPct * 100));
    // Factor 2: LTV:CAC ratio (0-30 points)
    const ltvCacScore = Math.min(30, Math.max(0, ltvCacRatio * 10));
    // Factor 3: Cash collection speed (0-30 points) - faster is better
    const collectionScore = Math.min(30, Math.max(0, 30 * (1 / Math.max(1, cashCollectionSpeed)) * 3));
    scalabilityScore = Math.round(marginScore + ltvCacScore + collectionScore);
    // Scalability assessment
    let scalabilityAssessment = '';
    if (scalabilityScore >= 80) {
      scalabilityAssessment = 'Highly Scalable';
    } else if (scalabilityScore >= 60) {
      scalabilityAssessment = 'Scalable';
    } else if (scalabilityScore >= 40) {
      scalabilityAssessment = 'Moderately Scalable';
    } else {
      scalabilityAssessment = 'Difficult to Scale';
    }
    return {
      steadyStateMonthlyRevenue,
      annualRevenue,
      bookedCallsPerMonthNeeded,
      adSpendPerMonth,
      bookedCallsPerWeek,
      bookedCallsPerDay,
      closersNeeded,
      closersNeededRaw,
      monthsToSteady,
      costPerShow,
      costPerClientFromBooked,
      data,
      revenuePerCloser,
      commissionMonthly,
      compPerCloserMonthly,
      fulfillmentCostMonthly,
      operatingProfitMonthly,
      operatingMarginPct,
      // Split payment details
      upfrontAmount,
      remainderAmount,
      monthlyPaymentAmount,
      // Calculated fulfillment percentage
      calculatedFulfillmentPct,
      // Scalability metrics
      ltvCacRatio,
      cashCollectionSpeed,
      scalabilityScore,
      scalabilityAssessment,
      // Use the recalculated values that include all cost types
      salesAndMarketingCosts: actualSalesAndMarketingCosts,
      totalDirectCosts: actualTotalDirectCosts,
      initialOperatingProfitMonthly,
      fullyLoadedCAC,
      fullyLoadedLtvCacRatio,
      commissionPerClient
    };
  }, [salesVelocity, ltvTotal, ltvMonths, collectionMode, upfrontPct, remainderMonths, showRate, closeRate, costPerBookedCall, workdaysPerWeek, callsPerCloserPerDay, weeksPerMonth, roundUpClosers, commissionPct, commissionBase, fulfillmentPct, directCosts]);
  // Calculate potential improvement if metrics matched industry benchmarks
  const potentialImprovements = useMemo(() => {
    // Calculate current metrics
    const currentEffectiveConversion = showRate * closeRate;
    const currentBookedCallsNeeded = salesVelocity / currentEffectiveConversion;
    const currentAdSpend = currentBookedCallsNeeded * costPerBookedCall;
    // Calculate metrics with industry benchmarks
    const benchmarkEffectiveConversion = INDUSTRY_BENCHMARKS.showRate * INDUSTRY_BENCHMARKS.closeRate;
    const benchmarkBookedCallsNeeded = salesVelocity / benchmarkEffectiveConversion;
    const benchmarkAdSpend = benchmarkBookedCallsNeeded * INDUSTRY_BENCHMARKS.costPerBookedCall;
    // Calculate potential improvements for individual metrics
    // 1. Improved show rate only
    const improvedShowRateConversion = INDUSTRY_BENCHMARKS.showRate * closeRate;
    const improvedShowRateBookedCalls = salesVelocity / improvedShowRateConversion;
    const improvedShowRateAdSpend = improvedShowRateBookedCalls * costPerBookedCall;
    const showRateAdSpendSavings = currentAdSpend - improvedShowRateAdSpend;
    // 2. Improved close rate only
    const improvedCloseRateConversion = showRate * INDUSTRY_BENCHMARKS.closeRate;
    const improvedCloseRateBookedCalls = salesVelocity / improvedCloseRateConversion;
    const improvedCloseRateAdSpend = improvedCloseRateBookedCalls * costPerBookedCall;
    const closeRateAdSpendSavings = currentAdSpend - improvedCloseRateAdSpend;
    // 3. Improved cost per booked call only
    const improvedCostPerBookedCallAdSpend = currentBookedCallsNeeded * INDUSTRY_BENCHMARKS.costPerBookedCall;
    const costPerBookedCallSavings = currentAdSpend - improvedCostPerBookedCallAdSpend;
    // 4. All metrics improved to benchmark
    const allImprovedAdSpend = benchmarkAdSpend;
    const allImprovedAdSpendSavings = currentAdSpend - allImprovedAdSpend;
    // Calculate revenue impact with same sales velocity but improved profit margin
    const currentOperatingMargin = derived.operatingMarginPct;
    // Calculate what revenue would be with benchmark metrics but same sales velocity
    const benchmarkOperatingProfit = derived.steadyStateMonthlyRevenue - benchmarkAdSpend - derived.commissionMonthly - derived.steadyStateMonthlyRevenue * INDUSTRY_BENCHMARKS.fulfillmentPct;
    const benchmarkOperatingMargin = derived.steadyStateMonthlyRevenue > 0 ? benchmarkOperatingProfit / derived.steadyStateMonthlyRevenue : 0;
    const marginImprovement = benchmarkOperatingMargin - currentOperatingMargin;
    // Calculate what happens if we maintain the same operating profit but increase sales velocity
    const currentOperatingProfit = derived.operatingProfitMonthly;
    // How many more clients could we sell with the ad spend savings?
    const additionalClientsPossible = benchmarkEffectiveConversion > 0 ? allImprovedAdSpendSavings / (INDUSTRY_BENCHMARKS.costPerBookedCall / benchmarkEffectiveConversion) : 0;
    // What would revenue be with additional clients?
    const additionalRevenue = additionalClientsPossible * (ltvTotal / Math.max(1, ltvMonths)) * (collectionMode === 'upfront' ? 1 : collectionMode === 'equal' ? 1 : 1);
    // What would profit be with additional clients?
    const additionalProfit = additionalRevenue * benchmarkOperatingMargin;
    // Total potential monthly profit improvement
    const totalProfitImprovement = additionalProfit + derived.steadyStateMonthlyRevenue * marginImprovement;
    return {
      // Current metrics
      currentEffectiveConversion,
      currentBookedCallsNeeded,
      currentAdSpend,
      currentOperatingMargin,
      currentOperatingProfit,
      // Benchmark metrics
      benchmarkEffectiveConversion,
      benchmarkBookedCallsNeeded,
      benchmarkAdSpend,
      benchmarkOperatingMargin,
      benchmarkOperatingProfit,
      // Individual improvements
      showRateAdSpendSavings,
      closeRateAdSpendSavings,
      costPerBookedCallSavings,
      allImprovedAdSpendSavings,
      // Revenue and profit impact
      marginImprovement,
      additionalClientsPossible,
      additionalRevenue,
      additionalProfit,
      totalProfitImprovement
    };
  }, [showRate, closeRate, costPerBookedCall, salesVelocity, ltvTotal, ltvMonths, collectionMode, derived.steadyStateMonthlyRevenue, derived.operatingMarginPct, derived.commissionMonthly, derived.operatingProfitMonthly]);
  // Update fulfillment percentage when direct costs change
  useEffect(() => {
    if (derived.steadyStateMonthlyRevenue > 0) {
      setFulfillmentPct(derived.calculatedFulfillmentPct);
    }
  }, [derived.calculatedFulfillmentPct, derived.steadyStateMonthlyRevenue]);
  const reset = () => {
    setSalesVelocity(40);
    setLtvTotal(6000);
    setLtvMonths(6);
    setCollectionMode('equal');
    setUpfrontPct(0.3);
    setRemainderMonths(5);
    setShowRate(0.7);
    setCloseRate(0.25);
    setCostPerBookedCall(150);
    setWorkdaysPerWeek(5);
    setWeeksPerMonth(4.33);
    setCallsPerCloserPerDay(6);
    setRoundUpClosers(true);
    setCommissionPct(0.1);
    setCommissionBase('ltv');
    setFulfillmentPct(0.3);
    setDirectCosts([{
      id: 1,
      name: 'Staff Salaries',
      quantity: 1,
      amount: 2000
    }, {
      id: 2,
      name: 'Software Tools',
      quantity: 1,
      amount: 500
    }, {
      id: 3,
      name: 'Materials',
      quantity: 1,
      amount: 800
    }]);
  };
  // --- Export helpers ---
  async function handleDownloadSnapshot() {
    try {
      if (!containerRef.current) return;
      const dataUrl = await toPng(containerRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff'
      });
      const link = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      link.download = `planner-snapshot-${ts}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Snapshot failed', err);
      alert('Sorry—could not capture a snapshot. Try a different browser or reduce content height.');
    }
  }
  async function handleDownloadPDF() {
    try {
      if (!containerRef.current) return;
      // Render the container to a high-res canvas
      const canvas = await toCanvas(containerRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      // A4 size in mm
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth(); // 210mm
      const pageHeight = pdf.internal.pageSize.getHeight(); // 297mm
      // Scale image to fit page width
      const imgWidth = pageWidth;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight; // shift up to show next slice
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      pdf.save(`planner-snapshot-${ts}.pdf`);
    } catch (err) {
      console.error('PDF export failed', err);
      alert('Sorry—could not create a PDF. Try a different browser or reduce content height.');
    }
  }
  // --- Minimal smoke tests (console) ---
  useEffect(() => {
    // These don't affect UI; they help catch regressions.
    // Test 1: Equal collection math
    const sv = 40,
      ltv = 6000,
      months = 6,
      show = 0.7,
      close = 0.25,
      cpbc = 150;
    const monthly = sv * (ltv / months); // 40 * 1000 = 40000
    const conv = show * close; // 0.175
    const booked = sv / conv; // ~228.5714
    const ad = booked * cpbc; // ~34285.714
    const approx = (a: number, b: number, t = 1e-3) => Math.abs((a - b) / Math.max(1, b)) < t;
    console.assert(approx(monthly, 40000), 'Test1 monthly revenue failed');
    console.assert(approx(booked, 228.5714), 'Test1 booked calls failed');
    console.assert(approx(ad, 34285.714), 'Test1 ad spend failed');
    // Test 2: CAC identities
    const cac1 = cpbc / conv;
    const cac2 = ad / sv;
    console.assert(approx(cac1, cac2), 'Test2 CAC identity failed');
  }, []);
  // Generate data for cost structure pie chart
  const costStructureData = useMemo(() => {
    const adSpend = derived.adSpendPerMonth;
    const commissions = derived.commissionMonthly;
    const directCosts = totalDirectCosts;
    const profit = derived.operatingProfitMonthly;
    // Filter out zero or negative values
    const data = [{
      name: 'Ad Spend',
      value: adSpend > 0 ? adSpend : 0
    }, {
      name: 'Commissions',
      value: commissions > 0 ? commissions : 0
    }, {
      name: 'Direct Costs',
      value: directCosts > 0 ? directCosts : 0
    }, {
      name: 'Net Profit',
      value: profit > 0 ? profit : 0
    }].filter(item => item.value > 0);
    return data;
  }, [derived.adSpendPerMonth, derived.commissionMonthly, totalDirectCosts, derived.operatingProfitMonthly]);
  // Generate scaling projection data
  const scalingProjectionData = useMemo(() => {
    const baseVelocity = salesVelocity;
    const velocities = [1, 5, 10, 25, 50, 100].map(multiplier => Math.max(1, Math.round(baseVelocity * (multiplier / 10))));
    return velocities.map(velocity => {
      // Calculate revenue for this velocity
      let monthlyRevenue = 0;
      if (collectionMode === 'equal') {
        monthlyRevenue = velocity * (ltvTotal / Math.max(1, ltvMonths));
      } else if (collectionMode === 'upfront') {
        monthlyRevenue = velocity * ltvTotal;
      } else {
        // split mode
        const upfront = ltvTotal * upfrontPct;
        const remEach = remainderMonths > 0 ? ltvTotal * (1 - upfrontPct) / remainderMonths : 0;
        monthlyRevenue = velocity * (upfront + remEach * Math.max(1, remainderMonths));
      }
      // Calculate costs
      const effShow = Math.max(1e-6, showRate);
      const effClose = Math.max(1e-6, closeRate);
      const bookedCallsNeeded = velocity / (effShow * effClose);
      const adSpend = bookedCallsNeeded * Math.max(0, costPerBookedCall);
      const commission = commissionBase === 'ltv' ? velocity * commissionPct * ltvTotal : commissionPct * monthlyRevenue;
      const directCost = totalDirectCosts * (velocity / Math.max(1, salesVelocity));
      const grossProfit = monthlyRevenue - directCost;
      const netProfit = monthlyRevenue - adSpend - commission - directCost;
      return {
        clients: velocity,
        revenue: monthlyRevenue,
        grossProfit: grossProfit > 0 ? grossProfit : 0,
        netProfit: netProfit > 0 ? netProfit : 0
      };
    });
  }, [salesVelocity, ltvTotal, ltvMonths, collectionMode, upfrontPct, remainderMonths, showRate, closeRate, costPerBookedCall, commissionPct, commissionBase, totalDirectCosts]);
  // Custom active shape for pie chart
  const renderActiveShape = props => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value
    } = props;
    return <g data-id="element-14">
        <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 6} startAngle={startAngle} endAngle={endAngle} fill={fill} data-id="element-15" />
        <text x={cx} y={cy - 15} dy={8} textAnchor="middle" fill="#888" data-id="element-16">
          {payload.name}
        </text>
        <text x={cx} y={cy + 15} dy={8} textAnchor="middle" fill="#333" fontWeight="bold" data-id="element-17">
          {fmtCurrency(value)} ({(percent * 100).toFixed(1)}%)
        </text>
      </g>;
  };
  // State for active pie slice
  const [activePieIndex, setActivePieIndex] = useState(0);
  return <TooltipProvider data-id="element-18">
      <div ref={containerRef} className="w-full max-w-6xl mx-auto p-3 sm:p-6 space-y-6 sm:space-y-8 bg-gradient-to-b from-gray-50 to-white min-h-screen font-sans" data-id="element-19">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-20">
          <div data-id="element-21">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-800 mb-1" data-id="element-22">
              Revenue & Resource Planner
            </h1>
            <p className="text-xs sm:text-sm text-gray-500" data-id="element-23">
              Model your revenue projections, required ad spend, and staffing
              needs based on your sales metrics.
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3" data-id="element-24">
            <Button onClick={handleDownloadSnapshot} className="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 text-xs sm:text-sm py-1.5 sm:py-2 px-2 sm:px-4" data-id="element-25">
              Download PNG
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-xs sm:text-sm py-1.5 sm:py-2 px-2 sm:px-4" data-id="element-26">
              Export PDF
            </Button>
          </div>
        </div>
        {/* Inputs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6" data-id="element-27">
          <Card className="md:col-span-5 border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-28">
            <CardContent className="p-3 sm:p-5 space-y-4 sm:space-y-5" data-id="element-29">
              <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center" data-id="element-30">
                <span className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-700 mr-2 text-xs" data-id="element-31">
                  1
                </span>
                Revenue Model
              </h2>
              <div className="space-y-2 sm:space-y-3" data-id="element-32">
                <Label className="text-gray-700" data-id="element-33">
                  Sales Velocity (clients sold / month)
                </Label>
                <Input type="number" value={salesVelocity} onChange={e => setSalesVelocity(parseFloat(e.target.value || '0'))} min={0} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-34" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-35">
                <div className="space-y-2 sm:space-y-3 col-span-1 sm:col-span-2" data-id="element-36">
                  <Label className="text-gray-700" data-id="element-37">Total LTV ($)</Label>
                  <Input type="number" value={ltvTotal} onChange={e => setLtvTotal(parseFloat(e.target.value || '0'))} min={0} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-38" />
                </div>
                {collectionMode === 'equal' && <div className="space-y-2 sm:space-y-3 col-span-1 sm:col-span-2" data-id="element-39">
                    <Label className="text-gray-700" data-id="element-40">
                      Months to Collect (equal mode)
                    </Label>
                    <Input type="number" value={ltvMonths} onChange={e => setLtvMonths(parseInt(e.target.value || '1'))} min={1} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-41" />
                  </div>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-42">
                <div className="space-y-2 sm:space-y-3 col-span-1 sm:col-span-3" data-id="element-43">
                  <Label className="text-gray-700" data-id="element-44">Collection Mode</Label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" value={collectionMode} onChange={e => setCollectionMode(e.target.value as any)} data-id="element-45">
                    <option value="upfront" data-id="element-46">100% upfront (lump sum)</option>
                    <option value="equal" data-id="element-47">Equal monthly payments</option>
                    <option value="split" data-id="element-48">Split: upfront + installments</option>
                  </select>
                </div>
                {collectionMode === 'split' && <>
                    <div className="space-y-2 sm:space-y-3" data-id="element-49">
                      <Label className="text-gray-700" data-id="element-50">Upfront % (0–1)</Label>
                      <Input type="number" step="0.01" value={upfrontPct} onChange={e => setUpfrontPct(Math.min(1, Math.max(0, parseFloat(e.target.value || '0'))))} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-51" />
                      <div className="text-xs text-gray-600 mt-1" data-id="element-52">
                        Upfront amount:{' '}
                        <span className="font-medium" data-id="element-53">
                          {fmtCurrency(derived.upfrontAmount)}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2 sm:space-y-3 col-span-1 sm:col-span-2" data-id="element-54">
                      <Label className="text-gray-700" data-id="element-55">Remainder Months</Label>
                      <Input type="number" value={remainderMonths} onChange={e => setRemainderMonths(parseInt(e.target.value || '1'))} min={1} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-56" />
                      <div className="text-xs text-gray-600 mt-1" data-id="element-57">
                        <div className="flex justify-between" data-id="element-58">
                          <span data-id="element-59">Remainder amount:</span>
                          <span className="font-medium" data-id="element-60">
                            {fmtCurrency(derived.remainderAmount)}
                          </span>
                        </div>
                        <div className="flex justify-between mt-1" data-id="element-61">
                          <span data-id="element-62">Monthly payment:</span>
                          <span className="font-medium" data-id="element-63">
                            {fmtCurrency(derived.monthlyPaymentAmount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>}
              </div>
            </CardContent>
          </Card>
          <Card className="md:col-span-4 border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-64">
            <CardContent className="p-5 space-y-5" data-id="element-65">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center" data-id="element-66">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-700 mr-2 text-xs" data-id="element-67">
                  2
                </span>
                Funnel Metrics
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-68">
                <div className="space-y-3" data-id="element-69">
                  <Label className="text-gray-700" data-id="element-70">Show Rate (0–1)</Label>
                  <Input type="number" step="0.01" value={showRate} onChange={e => setShowRate(parseFloat(e.target.value || '0'))} min={0} max={1} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-71" />
                </div>
                <div className="space-y-3" data-id="element-72">
                  <Label className="text-gray-700" data-id="element-73">Close Rate (0–1)</Label>
                  <Input type="number" step="0.01" value={closeRate} onChange={e => setCloseRate(parseFloat(e.target.value || '0'))} min={0} max={1} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-74" />
                </div>
              </div>
              <div className="space-y-3" data-id="element-75">
                <Label className="text-gray-700" data-id="element-76">
                  Cost per Booked Call ($)
                </Label>
                <Input type="number" value={costPerBookedCall} onChange={e => setCostPerBookedCall(parseFloat(e.target.value || '0'))} min={0} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-77" />
              </div>
              <div className="pt-2 text-xs text-gray-500 border-t border-gray-100 mt-2" data-id="element-78">
                <div className="flex items-center justify-between mb-1" data-id="element-79">
                  <span data-id="element-80">Effective Conversion:</span>
                  <span className="font-medium" data-id="element-81">
                    {(showRate * closeRate * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-82">
                  <span data-id="element-83">Cost Per Acquisition:</span>
                  <span className="font-medium" data-id="element-84">
                    {fmtCurrency(derived.costPerClientFromBooked)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="md:col-span-3 border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-85">
            <CardContent className="p-5 space-y-5" data-id="element-86">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center" data-id="element-87">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-700 mr-2 text-xs" data-id="element-88">
                  3
                </span>
                Capacity Planning
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-89">
                <div className="space-y-3" data-id="element-90">
                  <Label className="text-gray-700" data-id="element-91">Workdays / Week</Label>
                  <Input type="number" value={workdaysPerWeek} onChange={e => setWorkdaysPerWeek(parseFloat(e.target.value || '1'))} min={1} max={7} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-92" />
                </div>
                <div className="space-y-3" data-id="element-93">
                  <Label className="text-gray-700" data-id="element-94">Weeks / Month</Label>
                  <Input type="number" step="0.01" value={weeksPerMonth} onChange={e => setWeeksPerMonth(parseFloat(e.target.value || '4.33'))} min={4} max={5} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-95" />
                </div>
              </div>
              <div className="space-y-3" data-id="element-96">
                <Label className="text-gray-700" data-id="element-97">
                  Calls per Closer per Day
                </Label>
                <Input type="number" value={callsPerCloserPerDay} onChange={e => setCallsPerCloserPerDay(parseFloat(e.target.value || '1'))} min={1} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-98" />
              </div>
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between pt-4 gap-3" data-id="element-99">
                <div className="flex items-center gap-3" data-id="element-100">
                  <Switch checked={roundUpClosers} onCheckedChange={setRoundUpClosers} className={roundUpClosers ? 'bg-indigo-600' : 'bg-gray-200'} data-id="element-101" />
                  <span className="text-sm text-gray-600" data-id="element-102">
                    Round up closers
                  </span>
                </div>
                <Button variant="secondary" onClick={reset} className="bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs px-3 py-1" data-id="element-103">
                  Reset All
                </Button>
              </div>
            </CardContent>
          </Card>
          {/* Direct Costs Calculator */}
          <Card className="md:col-span-12 border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-104">
            <CardContent className="p-5 space-y-4" data-id="element-105">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center" data-id="element-106">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-700 mr-2 text-xs" data-id="element-107">
                  4
                </span>
                Monthly Direct Costs
              </h2>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-108">
                <div className="grid grid-cols-1 gap-4" data-id="element-109">
                  <div className="overflow-hidden bg-white rounded-lg border border-gray-200" data-id="element-110">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-111">
                      <div className="flex items-center justify-between" data-id="element-112">
                        <span className="font-medium text-gray-700 flex-1" data-id="element-113">
                          Monthly Cost Item
                        </span>
                        <span className="font-medium text-gray-700 w-24 text-center" data-id="element-114">
                          Quantity
                        </span>
                        <span className="font-medium text-gray-700 w-24 text-center" data-id="element-115">
                          Category
                        </span>
                        <span className="font-medium text-gray-700 w-32 text-center" data-id="element-116">
                          Cost Type
                        </span>
                        <span className="font-medium text-gray-700 w-40 text-right" data-id="element-117">
                          Amount
                        </span>
                        <span className="w-16" data-id="element-118"></span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100" data-id="element-119">
                      {directCosts.map(cost => <div key={cost.id} className={`px-4 py-3 flex items-center justify-between ${cost.category === 'Sales and Marketing' ? 'bg-blue-50' : ''}`} data-id="element-120">
                          {editingCostId === cost.id ?
                      // Edit mode
                      <>
                              <div className="flex-1 pr-2" data-id="element-121">
                                <Input type="text" value={editCostName} onChange={e => setEditCostName(e.target.value)} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-122" />
                              </div>
                              <div className="w-24 px-2" data-id="element-123">
                                <Input type="number" value={editCostQuantity} onChange={e => setEditCostQuantity(Math.max(1, parseInt(e.target.value || '1')))} min={1} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-124" />
                              </div>
                              <div className="w-24 px-2" data-id="element-125">
                                <select value={editCostCategory} onChange={e => setEditCostCategory(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-126">
                                  <option value="Sales and Marketing" data-id="element-127">
                                    Sales & Marketing
                                  </option>
                                  <option value="Other" data-id="element-128">Other</option>
                                </select>
                              </div>
                              <div className="w-32 px-2" data-id="element-129">
                                <select value={editCostType} onChange={e => setEditCostType(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-130">
                                  <option value="fixed" data-id="element-131">Fixed Cost</option>
                                  <option value="percentage" data-id="element-132">
                                    % of Revenue
                                  </option>
                                  <option value="profit-percentage" data-id="element-133">
                                    % of Profit
                                  </option>
                                </select>
                              </div>
                              <div className="w-40 pl-2 flex items-center" data-id="element-134">
                                <Input type={editCostType === 'fixed' ? 'number' : editCostType === 'percentage' ? 'number' : 'number'} value={editCostAmount} onChange={e => setEditCostAmount(parseFloat(e.target.value || '0'))} min={0} step={editCostType === 'percentage' ? '0.01' : '1'} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-135" />
                                {editCostType === 'percentage' && <span className="ml-1 text-gray-500" data-id="element-136">%</span>}
                              </div>
                              <div className="w-16 flex items-center justify-end space-x-1" data-id="element-137">
                                <button onClick={saveEditedCost} className="text-green-500 hover:text-green-700 transition-colors p-1" title="Save" data-id="element-138">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-139">
                                    <path d="M5 13l4 4L19 7" data-id="element-140"></path>
                                  </svg>
                                </button>
                                <button onClick={cancelEditing} className="text-gray-500 hover:text-gray-700 transition-colors p-1" title="Cancel" data-id="element-141">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-142">
                                    <line x1="18" y1="6" x2="6" y2="18" data-id="element-143"></line>
                                    <line x1="6" y1="6" x2="18" y2="18" data-id="element-144"></line>
                                  </svg>
                                </button>
                              </div>
                            </> :
                      // View mode
                      <>
                              <span className="text-gray-700 flex-1" data-id="element-145">
                                {cost.name}
                              </span>
                              <span className="text-gray-700 w-24 text-center" data-id="element-146">
                                {cost.quantity}
                              </span>
                              <span className={`w-24 text-center ${cost.category === 'Sales and Marketing' ? 'text-blue-700 font-medium' : 'text-gray-700'}`} data-id="element-147">
                                {cost.category === 'Sales and Marketing' ? 'S&M' : 'Other'}
                              </span>
                              <span className="text-gray-700 w-32 text-center" data-id="element-148">
                                {cost.costType === 'fixed' ? 'Fixed Cost' : cost.costType === 'percentage' ? '% of Revenue' : '% of Profit'}
                              </span>
                              <div className="flex items-center w-40 justify-end" data-id="element-149">
                                <span className="font-medium text-gray-800 mr-3" data-id="element-150">
                                  {cost.costType === 'fixed' ? fmtCurrency(cost.quantity * cost.amount) : cost.costType === 'percentage' ? derived.steadyStateMonthlyRevenue > 0 ? fmtCurrency(cost.amount / 100 * derived.steadyStateMonthlyRevenue) : '$0' : derived.initialOperatingProfitMonthly > 0 ? fmtCurrency(cost.amount / 100 * derived.initialOperatingProfitMonthly * cost.quantity) : '$0'}
                                </span>
                                <span className="text-xs text-gray-500" data-id="element-151">
                                  {cost.costType !== 'fixed' && `(${cost.amount}%)`}
                                </span>
                              </div>
                              <div className="w-16 flex items-center justify-end space-x-1" data-id="element-152">
                                <button onClick={() => startEditingCost(cost)} className="text-blue-500 hover:text-blue-700 transition-colors p-1" title="Edit" data-id="element-153">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-154">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-155"></path>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-156"></path>
                                  </svg>
                                </button>
                                <button onClick={() => removeDirectCost(cost.id)} className="text-red-500 hover:text-red-700 transition-colors p-1" title="Delete" data-id="element-157">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-158">
                                    <path d="M3 6h18" data-id="element-159"></path>
                                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" data-id="element-160"></path>
                                    <line x1="10" y1="11" x2="10" y2="17" data-id="element-161"></line>
                                    <line x1="14" y1="11" x2="14" y2="17" data-id="element-162"></line>
                                  </svg>
                                </button>
                              </div>
                            </>}
                        </div>)}
                      {/* Fixed Sales Commission Line */}
                      <div className="px-4 py-3 bg-blue-50 flex items-center justify-between" data-id="element-163">
                        <span className="text-gray-700 flex-1" data-id="element-164">
                          Sales Commissions
                        </span>
                        <span className="text-gray-700 w-24 text-center" data-id="element-165">
                          {salesVelocity}
                        </span>
                        <span className="w-24 text-center text-blue-700 font-medium" data-id="element-166">
                          S&M
                        </span>
                        <span className="text-gray-700 w-32 text-center" data-id="element-167">
                          {commissionBase === 'ltv' ? 'Per LTV' : '% of Revenue'}
                        </span>
                        <div className="flex items-center w-40 justify-end" data-id="element-168">
                          <span className="font-medium text-gray-800 mr-3" data-id="element-169">
                            {fmtCurrency(derived.commissionMonthly)}
                          </span>
                          <span className="text-xs text-gray-500" data-id="element-170">
                            ({(commissionPct * 100).toFixed(0)}%)
                          </span>
                        </div>
                        <div className="w-16" data-id="element-171"></div>
                      </div>
                      <div className="px-4 py-3 bg-gray-50 flex items-center justify-between" data-id="element-172">
                        <span className="font-bold text-gray-800" data-id="element-173">
                          Total Monthly Direct Costs
                        </span>
                        <span className="font-bold text-gray-800" data-id="element-174">
                          {fmtCurrency(totalDirectCosts)}
                        </span>
                      </div>
                      <div className="px-4 py-3 bg-blue-50 flex items-center justify-between" data-id="element-175">
                        <span className="font-medium text-blue-800" data-id="element-176">
                          Sales & Marketing Costs
                        </span>
                        <span className="font-bold text-blue-800" data-id="element-177">
                          {fmtCurrency(salesAndMarketingCosts + derived.commissionMonthly)}
                        </span>
                      </div>
                      <div className="px-4 py-3 bg-indigo-50 flex items-center justify-between" data-id="element-178">
                        <span className="font-medium text-indigo-800" data-id="element-179">
                          Fulfillment % of Monthly Revenue
                        </span>
                        <span className="font-bold text-indigo-800" data-id="element-180">
                          {derived.steadyStateMonthlyRevenue > 0 ? (derived.calculatedFulfillmentPct * 100).toFixed(1) + '%' : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-181">
                    <div className="md:col-span-2" data-id="element-182">
                      <Label className="text-gray-700" data-id="element-183">
                        Monthly Cost Item Name
                      </Label>
                      <Input type="text" value={newCostName} onChange={e => setNewCostName(e.target.value)} placeholder="e.g., Staff, Equipment, Software" className="focus:border-indigo-500 focus:ring-indigo-500 mt-1" data-id="element-184" />
                    </div>
                    <div data-id="element-185">
                      <Label className="text-gray-700" data-id="element-186">Quantity</Label>
                      <Input type="number" value={newCostQuantity} onChange={e => setNewCostQuantity(Math.max(1, parseInt(e.target.value || '1')))} min={1} className="focus:border-indigo-500 focus:ring-indigo-500 mt-1" data-id="element-187" />
                    </div>
                    <div data-id="element-188">
                      <Label className="text-gray-700" data-id="element-189">Category</Label>
                      <select value={newCostCategory} onChange={e => setNewCostCategory(e.target.value)} className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-190">
                        <option value="Sales and Marketing" data-id="element-191">
                          Sales & Marketing
                        </option>
                        <option value="Other" data-id="element-192">Other</option>
                      </select>
                    </div>
                    <div className="md:col-span-2" data-id="element-193">
                      <div className="flex items-end gap-4" data-id="element-194">
                        <div className="flex-1" data-id="element-195">
                          <Label className="text-gray-700" data-id="element-196">Cost Type</Label>
                          <select value={newCostType} onChange={e => setNewCostType(e.target.value)} className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-197">
                            <option value="fixed" data-id="element-198">Fixed Cost</option>
                            <option value="percentage" data-id="element-199">% of Revenue</option>
                            <option value="profit-percentage" data-id="element-200">
                              % of Profit
                            </option>
                          </select>
                        </div>
                        <div className="flex-1" data-id="element-201">
                          <Label className="text-gray-700" data-id="element-202">
                            {newCostType === 'fixed' ? 'Amount ($)' : newCostType === 'percentage' ? 'Percentage (%)' : 'Percentage of Profit (%)'}
                          </Label>
                          <div className="flex items-center" data-id="element-203">
                            <Input type="number" value={newCostAmount} onChange={e => setNewCostAmount(parseFloat(e.target.value || '0'))} min={0} step={newCostType === 'percentage' ? '0.01' : '1'} className="focus:border-indigo-500 focus:ring-indigo-500 mt-1" data-id="element-204" />
                            {newCostType === 'percentage' && <span className="ml-1 text-gray-500 mt-1" data-id="element-205">%</span>}
                          </div>
                        </div>
                        <Button onClick={addDirectCost} className="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 mt-1" data-id="element-206">
                          Add Cost
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Comp & Costs */}
          <Card className="md:col-span-12 border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-207">
            <CardContent className="p-5 space-y-4" data-id="element-208">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center" data-id="element-209">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-700 mr-2 text-xs" data-id="element-210">
                  5
                </span>
                Compensation & Cost Structure
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-id="element-211">
                <div className="md:col-span-4 space-y-3" data-id="element-212">
                  <Label className="text-gray-700" data-id="element-213">Commission % (0–1)</Label>
                  <Input type="number" step="0.01" value={commissionPct} onChange={e => setCommissionPct(Math.min(1, Math.max(0, parseFloat(e.target.value || '0'))))} className="focus:border-indigo-500 focus:ring-indigo-500" data-id="element-214" />
                </div>
                <div className="md:col-span-4 space-y-3" data-id="element-215">
                  <Label className="text-gray-700" data-id="element-216">Commission Base</Label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" value={commissionBase} onChange={e => setCommissionBase(e.target.value as 'ltv' | 'collected')} data-id="element-217">
                    <option value="ltv" data-id="element-218">Total LTV at Close</option>
                    <option value="collected" data-id="element-219">% of Collected Cash</option>
                  </select>
                </div>
                <div className="md:col-span-4 space-y-3" data-id="element-220">
                  <Label className="text-gray-700" data-id="element-221">
                    Calculated Fulfillment Cost % of Revenue
                  </Label>
                  <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 font-medium" data-id="element-222">
                    {(derived.calculatedFulfillmentPct * 100).toFixed(1)}%
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Key Results Section */}
        <div className="my-8" data-id="element-223">
          <h2 className="text-xl font-bold mb-6 text-gray-800 border-b pb-2" data-id="element-224">
            Key Results
          </h2>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-225">
            <KpiCard title="Monthly Revenue" value={fmtCurrency(derived.steadyStateMonthlyRevenue)} subtitle={<>
                  <span data-id="element-227">Steady state</span>
                  <span className="font-medium text-gray-600" data-id="element-228">
                    {fmtCurrency(derived.annualRevenue)} / year
                  </span>
                </>} footnote={`Reaches steady state in ${derived.monthsToSteady} months`} borderColor="border-l-indigo-500" textColor="text-indigo-600" tooltipContent={<div data-id="element-229">
                  <p data-id="element-230">
                    The expected monthly revenue once your sales model reaches a
                    steady state.
                  </p>
                  <p className="mt-1" data-id="element-231">
                    Based on your collection terms and sales velocity.
                  </p>
                </div>} data-id="element-226" />
            <KpiCard title="Ad Spend Required" value={fmtCurrency(derived.adSpendPerMonth)} subtitle={<>
                  <span data-id="element-233">Monthly</span>
                  <span className="font-medium text-gray-600" data-id="element-234">
                    {fmtNum(derived.bookedCallsPerMonthNeeded, 1)} calls needed
                  </span>
                </>} footnote={`Cost per acquisition: ${fmtCurrency(derived.costPerClientFromBooked)}`} borderColor="border-l-amber-500" textColor="text-amber-600" tooltipContent={<div data-id="element-235">
                  <p data-id="element-236">
                    The monthly ad budget needed to generate enough booked
                    calls.
                  </p>
                  <p className="mt-1" data-id="element-237">
                    Calculated as: booked calls needed × cost per booked call.
                  </p>
                </div>} data-id="element-232" />
            <CACCard cac={derived.costPerClientFromBooked} fullyLoadedCAC={derived.fullyLoadedCAC} ltvCacRatio={derived.ltvCacRatio} fullyLoadedLtvCacRatio={derived.fullyLoadedLtvCacRatio} ltv={ltvTotal} showRate={showRate} closeRate={closeRate} costPerBookedCall={costPerBookedCall} costPerShow={derived.costPerShow} salesAndMarketingCosts={derived.salesAndMarketingCosts} commissionPerClient={derived.commissionPerClient} data-id="element-238" />
            <ProfitCard operatingProfitMonthly={derived.operatingProfitMonthly} operatingMarginPct={derived.operatingMarginPct} metrics={derived} inputs={{
            salesVelocity,
            ltvTotal,
            ltvMonths,
            showRate,
            closeRate,
            costPerBookedCall,
            fulfillmentPct: derived.calculatedFulfillmentPct,
            commissionPct,
            collectionMode
          }} data-id="element-239" />
            <ScalabilityCard score={derived.scalabilityScore} assessment={derived.scalabilityAssessment} ltvCacRatio={derived.ltvCacRatio} operatingMarginPct={derived.operatingMarginPct} cashCollectionSpeed={derived.cashCollectionSpeed} data-id="element-240" />
          </div>
        </div>
        {/* Detailed Metrics Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-241">
          <Card className="border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-242">
            <CardContent className="p-5 space-y-4" data-id="element-243">
              <div className="flex items-center" data-id="element-244">
                <h2 className="text-lg font-semibold text-gray-800" data-id="element-245">
                  Ad Spend Breakdown
                </h2>
                <Tooltip data-id="element-246">
                  <TooltipTrigger className="group" data-id="element-247">
                    <span className="w-5 h-5 ml-2 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-xs hover:bg-amber-200 transition-colors" data-id="element-248">
                      ?
                    </span>
                    <TooltipContent className="w-64" data-id="element-249">
                      <p data-id="element-250">
                        This breakdown shows how your ad spend is calculated
                        based on your funnel metrics and sales targets.
                      </p>
                    </TooltipContent>
                  </TooltipTrigger>
                </Tooltip>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 mb-4" data-id="element-251">
                <div className="flex flex-col gap-3" data-id="element-252">
                  <div className="flex items-center justify-between" data-id="element-253">
                    <div className="flex items-center" data-id="element-254">
                      <div className="w-3 h-3 rounded-full bg-amber-500 mr-2" data-id="element-255"></div>
                      <span className="text-sm text-gray-700" data-id="element-256">
                        Booked Calls Needed
                      </span>
                    </div>
                    <span className="font-semibold" data-id="element-257">
                      {fmtNum(derived.bookedCallsPerMonthNeeded, 1)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-258">
                    <div className="flex items-center" data-id="element-259">
                      <div className="w-3 h-3 rounded-full bg-amber-500 mr-2" data-id="element-260"></div>
                      <span className="text-sm text-gray-700" data-id="element-261">
                        Cost per Booked Call
                      </span>
                    </div>
                    <span className="font-semibold" data-id="element-262">
                      {fmtCurrency(costPerBookedCall)}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-amber-200 flex items-center justify-between" data-id="element-263">
                    <div className="flex items-center" data-id="element-264">
                      <div className="w-3 h-3 rounded-full bg-amber-600 mr-2" data-id="element-265"></div>
                      <span className="text-sm font-medium text-gray-800" data-id="element-266">
                        Monthly Ad Spend
                      </span>
                    </div>
                    <span className="font-bold text-lg" data-id="element-267">
                      {fmtCurrency(derived.adSpendPerMonth)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3" data-id="element-268">
                <Metric label="Booked Calls / Week" value={fmtNum(derived.bookedCallsPerWeek, 1)} className="bg-gray-50" data-id="element-269" />
                <Metric label="Booked Calls / Day" value={fmtNum(derived.bookedCallsPerDay, 1)} sub={`Based on ${workdaysPerWeek} workdays/week`} className="bg-gray-50" data-id="element-270" />
                <Metric label="Shows / Month" value={fmtNum(derived.bookedCallsPerMonthNeeded * Math.max(0, showRate), 1)} hint="Booked calls × show rate" className="bg-gray-50" data-id="element-271" />
                <Metric label="Deals / Month" value={fmtNum(salesVelocity, 0)} sub="Your sales velocity target" className="bg-gray-50" data-id="element-272" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-273">
            <CardContent className="p-5 space-y-4" data-id="element-274">
              <div className="flex items-center" data-id="element-275">
                <h2 className="text-lg font-semibold text-gray-800" data-id="element-276">
                  Staffing Requirements
                </h2>
                <Tooltip data-id="element-277">
                  <TooltipTrigger className="group" data-id="element-278">
                    <span className="w-5 h-5 ml-2 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-xs hover:bg-indigo-200 transition-colors" data-id="element-279">
                      ?
                    </span>
                    <TooltipContent className="w-64" data-id="element-280">
                      <p data-id="element-281">
                        This section shows how many sales staff you'll need
                        based on your call volume and capacity assumptions.
                      </p>
                    </TooltipContent>
                  </TooltipTrigger>
                </Tooltip>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4 mb-4" data-id="element-282">
                <div className="flex flex-col gap-3" data-id="element-283">
                  <div className="flex items-center justify-between" data-id="element-284">
                    <div className="flex items-center" data-id="element-285">
                      <div className="w-3 h-3 rounded-full bg-indigo-500 mr-2" data-id="element-286"></div>
                      <span className="text-sm text-gray-700" data-id="element-287">
                        Booked Calls / Day
                      </span>
                    </div>
                    <span className="font-semibold" data-id="element-288">
                      {fmtNum(derived.bookedCallsPerDay, 1)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-289">
                    <div className="flex items-center" data-id="element-290">
                      <div className="w-3 h-3 rounded-full bg-indigo-500 mr-2" data-id="element-291"></div>
                      <Tooltip data-id="element-292">
                        <TooltipTrigger data-id="element-293">
                          <span className="text-sm text-gray-700 border-b border-dotted border-gray-400" data-id="element-294">
                            Expected Calls / Closer / Day
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-800 text-white p-2" data-id="element-295">
                          Actual expected workload per closer based on booked
                          calls divided by number of closers
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <span className="font-semibold" data-id="element-296">
                      {fmtNum(derived.bookedCallsPerDay / Math.max(1, derived.closersNeeded), 1)}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-indigo-200 flex items-center justify-between" data-id="element-297">
                    <div className="flex items-center" data-id="element-298">
                      <div className="w-3 h-3 rounded-full bg-indigo-600 mr-2" data-id="element-299"></div>
                      <span className="text-sm font-medium text-gray-800" data-id="element-300">
                        Closers Needed
                      </span>
                    </div>
                    <span className="font-bold text-lg" data-id="element-301">
                      {fmtNum(derived.closersNeeded, roundUpClosers ? 0 : 2)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3" data-id="element-302">
                <Metric label="Revenue / Closer / Month" value={fmtCurrency(derived.revenuePerCloser)} className="bg-gray-50" data-id="element-303" />
                <Metric label="Commission / Closer / Month" value={fmtCurrency(derived.compPerCloserMonthly)} hint={commissionBase === 'ltv' ? 'Based on total LTV at close' : 'Based on collected cash'} className="bg-gray-50" data-id="element-304" />
                <Metric label="Effective Cost / Show" value={fmtCurrency(derived.costPerShow)} hint="Cost per booked call ÷ show rate" className="bg-gray-50" data-id="element-305" />
                <Metric label="Closer Capacity / Day" value={fmtNum(callsPerCloserPerDay, 1)} hint="Maximum calls a closer can handle per day" className="bg-gray-50" data-id="element-306" />
              </div>
            </CardContent>
          </Card>
        </div>
        {/* P&L Snapshot */}
        <Card className="border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-307">
          <CardContent className="p-5 space-y-4" data-id="element-308">
            <div className="flex items-center" data-id="element-309">
              <h2 className="text-lg font-semibold text-gray-800" data-id="element-310">
                P&L Snapshot (Monthly, steady-state)
              </h2>
              <Tooltip data-id="element-311">
                <TooltipTrigger className="group" data-id="element-312">
                  <span className="w-5 h-5 ml-2 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 text-xs hover:bg-gray-200 transition-colors" data-id="element-313">
                    ?
                  </span>
                  <TooltipContent className="w-64" data-id="element-314">
                    <p data-id="element-315">
                      A simplified profit and loss statement showing your key
                      revenue and expense categories at steady state.
                    </p>
                  </TooltipContent>
                </TooltipTrigger>
              </Tooltip>
            </div>
            <div className="overflow-hidden bg-white rounded-xl border border-gray-200" data-id="element-316">
              <div className="bg-gray-50 px-5 py-3 border-b border-gray-200" data-id="element-317">
                <div className="flex items-center justify-between" data-id="element-318">
                  <span className="font-medium text-gray-700" data-id="element-319">Monthly P&L</span>
                  <span className="text-sm text-gray-500" data-id="element-320">
                    Steady-state projections
                  </span>
                </div>
              </div>
              <div className="divide-y divide-gray-100" data-id="element-321">
                <PLItem label="Revenue" value={fmtCurrency(derived.steadyStateMonthlyRevenue)} type="revenue" hint="Total revenue at steady state based on your collection terms and sales velocity" data-id="element-322" />
                <PLItem label="Ad Spend" value={fmtCurrency(derived.adSpendPerMonth)} type="expense" hint="Cost to generate enough booked calls to achieve your sales velocity target" data-id="element-323" />
                <PLItem label="Sales Commissions" value={fmtCurrency(derived.commissionMonthly)} type="expense" hint={commissionBase === 'ltv' ? 'Based on total LTV at close' : 'Based on collected cash'} data-id="element-324" />
                <PLItem label="Monthly Direct Costs" value={fmtCurrency(totalDirectCosts)} type="expense" hint="Monthly costs to deliver your product or service (salaries, software, materials, etc.)" data-id="element-325" />
                <PLItem label="Operating Profit" value={fmtCurrency(derived.operatingProfitMonthly)} type="profit" bold={true} hint="Revenue minus all costs (ad spend, commissions, and fulfillment)" data-id="element-326" />
                <PLItem label="Operating Margin" value={`${fmtNum(derived.operatingMarginPct * 100, 1)}%`} type={derived.operatingMarginPct >= 0 ? 'profit' : 'expense'} hint="Operating profit as a percentage of revenue. Healthy businesses target 30%+ margins." data-id="element-327" />
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Chart */}
        <Card className="border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-328">
          <CardContent className="p-5 space-y-4" data-id="element-329">
            <div className="flex items-center justify-between" data-id="element-330">
              <h2 className="text-lg font-semibold text-gray-800" data-id="element-331">
                Revenue Ramp Timeline
              </h2>
              <div className="flex items-center gap-2" data-id="element-332">
                <div className="w-3 h-3 rounded-full bg-indigo-500" data-id="element-333"></div>
                <span className="text-sm text-gray-600" data-id="element-334">Monthly Revenue</span>
                <div className="w-3 h-3 rounded-full bg-emerald-300 ml-2" data-id="element-335"></div>
                <span className="text-sm text-gray-600" data-id="element-336">Steady State</span>
              </div>
            </div>
            <div className="w-full h-80 bg-white p-4 rounded-lg border border-gray-100" data-id="element-337">
              <ResponsiveContainer width="100%" height="100%" data-id="element-338">
                <LineChart data={derived.data} margin={{
                top: 20,
                right: 30,
                bottom: 20,
                left: 20
              }} data-id="element-339">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-340" />
                  <XAxis dataKey="month" tickFormatter={v => `Month ${v}`} stroke="#888" tick={{
                  fontSize: 12
                }} data-id="element-341" />
                  <YAxis tickFormatter={v => v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`} stroke="#888" tick={{
                  fontSize: 12
                }} data-id="element-342" />
                  <RTooltip content={({
                  active,
                  payload,
                  label
                }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return <div className="bg-white p-4 border border-gray-200 shadow-lg rounded-lg text-sm" data-id="element-344">
                            <div className="font-bold text-gray-800 border-b pb-2 mb-2" data-id="element-345">
                              Month {data.month} Financials
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-346">
                              <div className="text-gray-600" data-id="element-347">Revenue:</div>
                              <div className="font-medium text-right text-blue-600" data-id="element-348">
                                {fmtCurrency(data.revenue)}
                              </div>
                              <div className="text-gray-600" data-id="element-349">Ad Spend:</div>
                              <div className="font-medium text-right text-amber-600" data-id="element-350">
                                {fmtCurrency(data.adSpend)}
                              </div>
                              <div className="text-gray-600" data-id="element-351">Commissions:</div>
                              <div className="font-medium text-right" data-id="element-352">
                                {fmtCurrency(data.commissions)}
                              </div>
                              <div className="text-gray-600" data-id="element-353">Direct Costs:</div>
                              <div className="font-medium text-right" data-id="element-354">
                                {fmtCurrency(data.directCosts)}
                              </div>
                              <div className="text-gray-600 font-semibold" data-id="element-355">
                                Operating Profit:
                              </div>
                              <div className="font-bold text-right text-emerald-600" data-id="element-356">
                                {fmtCurrency(data.operatingProfit)}
                              </div>
                              <div className="text-gray-600" data-id="element-357">Margin:</div>
                              <div className="font-medium text-right" data-id="element-358">
                                {(data.operatingMargin * 100).toFixed(1)}%
                              </div>
                              <div className="border-t col-span-2 my-1" data-id="element-359"></div>
                              <div className="text-gray-600" data-id="element-360">New Clients:</div>
                              <div className="font-medium text-right" data-id="element-361">
                                {data.newClients}
                              </div>
                              <div className="text-gray-600" data-id="element-362">Booked Calls:</div>
                              <div className="font-medium text-right" data-id="element-363">
                                {fmtNum(data.bookedCalls, 1)}
                              </div>
                              <div className="text-gray-600" data-id="element-364">Shows:</div>
                              <div className="font-medium text-right" data-id="element-365">
                                {fmtNum(data.shows, 1)}
                              </div>
                            </div>
                          </div>;
                  }
                  return null;
                }} wrapperStyle={{
                  outline: 'none'
                }} data-id="element-343" />
                  <ReferenceLine y={derived.steadyStateMonthlyRevenue} stroke="#10b981" strokeDasharray="3 3" label={{
                  value: 'Steady State',
                  position: 'right',
                  fill: '#10b981',
                  fontSize: 12
                }} data-id="element-366" />
                  <Line type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={3} dot={{
                  r: 3,
                  fill: '#6366f1'
                }} activeDot={{
                  r: 6
                }} data-id="element-367" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded" data-id="element-368">
              <p data-id="element-369">
                This chart shows the revenue ramp over time as new client
                cohorts stack. The model reaches steady state by month{' '}
                {derived.monthsToSteady}. Hover over each data point to see
                detailed financials for that specific month.
              </p>
            </div>
          </CardContent>
        </Card>
        {/* Advanced Visualizations Section */}
        <div className="my-8" data-id="element-370">
          <h2 className="text-xl font-bold mb-6 text-gray-800 border-b pb-2" data-id="element-371">
            Advanced Visualizations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-372">
            {/* Cost Structure Breakdown */}
            <Card className="border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-373">
              <CardContent className="p-5 space-y-4" data-id="element-374">
                <div className="flex items-center justify-between" data-id="element-375">
                  <h2 className="text-lg font-semibold text-gray-800" data-id="element-376">
                    Cost Structure Breakdown
                  </h2>
                  <Tooltip data-id="element-377">
                    <TooltipTrigger className="group" data-id="element-378">
                      <span className="w-5 h-5 ml-2 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 text-xs hover:bg-gray-200 transition-colors" data-id="element-379">
                        ?
                      </span>
                      <TooltipContent className="w-64" data-id="element-380">
                        <p data-id="element-381">
                          This chart shows the breakdown of your monthly costs
                          and profit at your current sales velocity.
                        </p>
                      </TooltipContent>
                    </TooltipTrigger>
                  </Tooltip>
                </div>
                <div className="w-full h-80 bg-white rounded-lg" data-id="element-382">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-383">
                    <PieChart data-id="element-384">
                      <Pie activeIndex={activePieIndex} activeShape={renderActiveShape} data={costStructureData} cx="50%" cy="50%" innerRadius={70} outerRadius={100} fill="#8884d8" dataKey="value" onMouseEnter={(_, index) => setActivePieIndex(index)} data-id="element-385">
                        {costStructureData.map((entry, index) => {
                        const COLORS = ['#f59e0b', '#6366f1', '#8b5cf6', '#10b981'];
                        return <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-386" />;
                      })}
                      </Pie>
                      <Legend layout="vertical" verticalAlign="middle" align="right" iconType="circle" data-id="element-387" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded" data-id="element-388">
                  <p data-id="element-389">
                    At your current sales velocity of {salesVelocity}{' '}
                    clients/month, this is how your monthly revenue of{' '}
                    {fmtCurrency(derived.steadyStateMonthlyRevenue)} breaks
                    down.
                  </p>
                </div>
              </CardContent>
            </Card>
            {/* Scaling Projection */}
            <Card className="border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-390">
              <CardContent className="p-5 space-y-4" data-id="element-391">
                <div className="flex items-center justify-between" data-id="element-392">
                  <h2 className="text-lg font-semibold text-gray-800" data-id="element-393">
                    Scaling Projection
                  </h2>
                  <Tooltip data-id="element-394">
                    <TooltipTrigger className="group" data-id="element-395">
                      <span className="w-5 h-5 ml-2 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 text-xs hover:bg-gray-200 transition-colors" data-id="element-396">
                        ?
                      </span>
                      <TooltipContent className="w-64" data-id="element-397">
                        <p data-id="element-398">
                          This chart shows how your revenue and profit scale as
                          you increase your sales velocity.
                        </p>
                      </TooltipContent>
                    </TooltipTrigger>
                  </Tooltip>
                </div>
                <div className="flex items-center gap-4 flex-wrap" data-id="element-399">
                  <div className="flex items-center gap-2" data-id="element-400">
                    <div className="w-3 h-3 rounded-full bg-indigo-500" data-id="element-401"></div>
                    <span className="text-sm text-gray-600" data-id="element-402">Revenue</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-403">
                    <div className="w-3 h-3 rounded-full bg-purple-400" data-id="element-404"></div>
                    <span className="text-sm text-gray-600" data-id="element-405">Gross Profit</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-406">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-407"></div>
                    <span className="text-sm text-gray-600" data-id="element-408">Net Profit</span>
                  </div>
                </div>
                <div className="w-full h-80 bg-white rounded-lg" data-id="element-409">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-410">
                    <AreaChart data={scalingProjectionData} margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 10
                  }} data-id="element-411">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-412" />
                      <XAxis dataKey="clients" label={{
                      value: 'Clients per Month',
                      position: 'insideBottom',
                      offset: -5
                    }} data-id="element-413" />
                      <YAxis tickFormatter={v => v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`} data-id="element-414" />
                      <RTooltip formatter={value => fmtCurrency(Number(value))} labelFormatter={value => `${value} clients/month`} data-id="element-415" />
                      <Area type="monotone" dataKey="revenue" stackId="1" stroke="#6366f1" fill="#6366f1" fillOpacity={0.6} data-id="element-416" />
                      <Area type="monotone" dataKey="grossProfit" stackId="2" stroke="#a78bfa" fill="#a78bfa" fillOpacity={0.6} data-id="element-417" />
                      <Area type="monotone" dataKey="netProfit" stackId="3" stroke="#10b981" fill="#10b981" fillOpacity={0.6} data-id="element-418" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded" data-id="element-419">
                  <p data-id="element-420">
                    This projection shows how your business model scales as you
                    increase your sales velocity. Your current velocity is{' '}
                    {salesVelocity} clients/month.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        {/* Compare to Industry Averages Section */}
        <div className="my-8" data-id="element-421">
          <h2 className="text-xl font-bold mb-6 text-gray-800 border-b pb-2" data-id="element-422">
            Compare to Industry Averages
          </h2>
          <Card className="border-gray-200 hover:shadow-md transition-shadow duration-200 mb-6" data-id="element-423">
            <CardContent className="p-5 space-y-4" data-id="element-424">
              <div className="flex items-center justify-between" data-id="element-425">
                <h2 className="text-lg font-semibold text-gray-800" data-id="element-426">
                  Potential Improvements
                </h2>
                <Tooltip data-id="element-427">
                  <TooltipTrigger className="group" data-id="element-428">
                    <span className="w-5 h-5 ml-2 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 text-xs hover:bg-gray-200 transition-colors" data-id="element-429">
                      ?
                    </span>
                    <TooltipContent className="w-64" data-id="element-430">
                      <p data-id="element-431">
                        This section shows how much additional profit you could
                        generate if your metrics matched industry benchmarks.
                      </p>
                    </TooltipContent>
                  </TooltipTrigger>
                </Tooltip>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200" data-id="element-432">
                <h3 className="font-semibold text-amber-800 mb-2" data-id="element-433">
                  Money Left on the Table
                </h3>
                <div className="text-3xl font-bold text-amber-900 mb-3" data-id="element-434">
                  {fmtCurrency(Math.max(0, potentialImprovements.totalProfitImprovement))}
                  <span className="text-base font-normal text-amber-700 ml-2" data-id="element-435">
                    additional profit per month
                  </span>
                </div>
                <div className="text-xl font-bold text-amber-800 mb-3" data-id="element-436">
                  {fmtCurrency(Math.max(0, potentialImprovements.totalProfitImprovement * 12))}
                  <span className="text-sm font-normal text-amber-700 ml-2" data-id="element-437">
                    additional profit per year
                  </span>
                </div>
                <p className="text-sm text-amber-700 font-medium" data-id="element-438">
                  By improving your metrics to match industry benchmarks, you
                  could increase your monthly profit by up to{' '}
                  {fmtCurrency(Math.max(0, potentialImprovements.totalProfitImprovement))}
                  . This represents{' '}
                  {potentialImprovements.currentOperatingProfit > 0 ? `a ${fmtNum(Math.max(0, potentialImprovements.totalProfitImprovement / potentialImprovements.currentOperatingProfit * 100), 0)}% increase` : 'a significant improvement'}{' '}
                  in your current operating profit.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-id="element-439">
                {/* Show Rate Comparison */}
                <ComparisonCard title="Show Rate" currentValue={showRate} benchmarkValue={INDUSTRY_BENCHMARKS.showRate} format="percentage" improvement={fmtCurrency(Math.max(0, potentialImprovements.showRateAdSpendSavings))} improvementLabel="Ad spend savings" tooltipContent="The percentage of booked calls that actually show up. Improving this reduces your cost per acquisition." data-id="element-440" />
                {/* Close Rate Comparison */}
                <ComparisonCard title="Close Rate" currentValue={closeRate} benchmarkValue={INDUSTRY_BENCHMARKS.closeRate} format="percentage" improvement={fmtCurrency(Math.max(0, potentialImprovements.closeRateAdSpendSavings))} improvementLabel="Ad spend savings" tooltipContent="The percentage of showed calls that convert to paying clients. Improving this dramatically reduces your acquisition costs." data-id="element-441" />
                {/* Cost Per Booked Call Comparison */}
                <ComparisonCard title="Cost Per Booked Call" currentValue={costPerBookedCall} benchmarkValue={INDUSTRY_BENCHMARKS.costPerBookedCall} format="currency" improvement={fmtCurrency(Math.max(0, potentialImprovements.costPerBookedCallSavings))} improvementLabel="Monthly savings" tooltipContent="The average cost to generate a booked call. Lower is better. Improving this directly reduces your ad spend." inversed={true} data-id="element-442" />
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4 mt-4" data-id="element-443">
                <h3 className="font-semibold text-gray-800 mb-3" data-id="element-444">
                  Combined Impact of All Improvements
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-445">
                  <div className="space-y-2" data-id="element-446">
                    <div className="flex justify-between text-sm" data-id="element-447">
                      <span className="text-gray-600" data-id="element-448">
                        Current Monthly Ad Spend:
                      </span>
                      <span className="font-medium text-gray-800" data-id="element-449">
                        {fmtCurrency(potentialImprovements.currentAdSpend)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-450">
                      <span className="text-gray-600" data-id="element-451">Benchmark Ad Spend:</span>
                      <span className="font-medium text-green-600" data-id="element-452">
                        {fmtCurrency(potentialImprovements.benchmarkAdSpend)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-100" data-id="element-453">
                      <span className="text-gray-700 font-medium" data-id="element-454">
                        Total Ad Spend Savings:
                      </span>
                      <span className="font-bold text-emerald-600" data-id="element-455">
                        {fmtCurrency(Math.max(0, potentialImprovements.allImprovedAdSpendSavings))}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-456">
                    <div className="flex justify-between text-sm" data-id="element-457">
                      <span className="text-gray-600" data-id="element-458">
                        Current Operating Margin:
                      </span>
                      <span className="font-medium text-gray-800" data-id="element-459">
                        {fmtNum(potentialImprovements.currentOperatingMargin * 100, 1)}
                        %
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-460">
                      <span className="text-gray-600" data-id="element-461">
                        Benchmark Operating Margin:
                      </span>
                      <span className="font-medium text-green-600" data-id="element-462">
                        {fmtNum(potentialImprovements.benchmarkOperatingMargin * 100, 1)}
                        %
                      </span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-100" data-id="element-463">
                      <span className="text-gray-700 font-medium" data-id="element-464">
                        Additional Clients Possible:
                      </span>
                      <span className="font-bold text-blue-600" data-id="element-465">
                        {fmtNum(Math.max(0, potentialImprovements.additionalClientsPossible), 1)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-200" data-id="element-466">
                  <div className="flex justify-between items-center" data-id="element-467">
                    <span className="text-gray-700 font-medium" data-id="element-468">
                      Reinvesting Savings Could Generate:
                    </span>
                    <div className="text-right" data-id="element-469">
                      <div className="font-bold text-xl text-indigo-600" data-id="element-470">
                        {fmtCurrency(Math.max(0, potentialImprovements.additionalRevenue))}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-471">
                        additional monthly revenue
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-472">
            <CardContent className="p-5 space-y-4" data-id="element-473">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-474"></div>
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded mt-2" data-id="element-475">
                <p data-id="element-476">
                  <span className="font-medium text-gray-700" data-id="element-477">
                    Industry benchmarks
                  </span>{' '}
                  represent top-quartile performance in service businesses with
                  similar sales models. Your specific industry may vary. Focus
                  on the metrics with the biggest potential impact first.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Notes */}
        <Card className="border-gray-200" data-id="element-478">
          <CardContent className="p-5 space-y-3" data-id="element-479">
            <h3 className="text-base font-semibold text-gray-800" data-id="element-480">
              Model Notes & Assumptions
            </h3>
            <div className="text-sm text-gray-600 space-y-3" data-id="element-481">
              <p data-id="element-482">
                <strong className="text-gray-700" data-id="element-483">Funnel Assumptions:</strong>{' '}
                Booked call → show → close are treated as independent conversion
                steps. Required booked calls = target clients ÷ (show rate ×
                close rate).
              </p>
              <p data-id="element-484">
                <strong className="text-gray-700" data-id="element-485">Collection Modes:</strong>
                <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs" data-id="element-486">
                  Equal
                </span>{' '}
                = even payments over months;
                <span className="ml-1 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs" data-id="element-487">
                  Upfront
                </span>{' '}
                = 100% in month 1;
                <span className="ml-1 px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs" data-id="element-488">
                  Split
                </span>{' '}
                = upfront % + remainder over chosen months.
              </p>
              <p data-id="element-489">
                <strong className="text-gray-700" data-id="element-490">Direct Costs:</strong> The
                calculator uses itemized monthly direct costs (salaries,
                software, materials, etc.) to calculate a more accurate
                fulfillment cost percentage, which directly impacts your
                operating profit and margin.
              </p>
              <p data-id="element-491">
                <strong className="text-gray-700" data-id="element-492">Export Options:</strong> Use
                the buttons at the top to download a high-resolution PNG
                snapshot or a paginated PDF report of this calculator.
              </p>
            </div>
            <div className="text-xs text-gray-500 pt-2 italic" data-id="element-493">
              This calculator is for planning purposes only. Actual results may
              vary based on market conditions, team performance, and other
              factors.
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>;
}
// Helper components for better organization
function Metric({
  label,
  value,
  sub,
  hint,
  className = ''
}: {
  label: string;
  value: string | number;
  sub?: string;
  hint?: string;
  className?: string;
}) {
  return <div className={`rounded-xl border border-gray-200 p-3 transition-shadow duration-200 hover:shadow-md ${className}`} data-id="element-494">
      <div className="text-xs text-gray-500 flex items-center gap-2 mb-1" data-id="element-495">
        {label}
        {hint && <Tooltip data-id="element-496">
            <TooltipTrigger className="group" data-id="element-497">
              <span className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 text-gray-600 text-[10px] hover:bg-gray-300 transition-colors" data-id="element-498">
                ?
              </span>
              <TooltipContent data-id="element-499">{hint}</TooltipContent>
            </TooltipTrigger>
          </Tooltip>}
      </div>
      <div className="text-lg font-semibold text-gray-800" data-id="element-500">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-1" data-id="element-501">{sub}</div>}
    </div>;
}
function PLItem({
  label,
  value,
  type,
  bold = false,
  hint
}: {
  label: string;
  value: string;
  type: 'revenue' | 'expense' | 'profit';
  bold?: boolean;
  hint?: string;
}) {
  const getColor = () => {
    switch (type) {
      case 'revenue':
        return 'text-blue-600';
      case 'expense':
        return 'text-red-600';
      case 'profit':
        return 'text-emerald-600';
      default:
        return 'text-gray-800';
    }
  };
  return <div className="px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-502">
      <span className={`${bold ? 'font-medium' : ''} text-gray-700 flex items-center`} data-id="element-503">
        {label}
        {hint && <Tooltip data-id="element-504">
            <TooltipTrigger className="group" data-id="element-505">
              <span className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 text-gray-600 text-[10px] ml-1.5 hover:bg-gray-300 transition-colors" data-id="element-506">
                ?
              </span>
              <TooltipContent data-id="element-507">{hint}</TooltipContent>
            </TooltipTrigger>
          </Tooltip>}
      </span>
      <span className={`${getColor()} ${bold ? 'font-bold text-lg' : 'font-medium'}`} data-id="element-508">
        {value}
      </span>
    </div>;
}
function ProfitCard({
  operatingProfitMonthly,
  operatingMarginPct,
  metrics,
  inputs
}: {
  operatingProfitMonthly: number;
  operatingMarginPct: number;
  metrics: any;
  inputs: {
    salesVelocity: number;
    ltvTotal: number;
    ltvMonths: number;
    showRate: number;
    closeRate: number;
    costPerBookedCall: number;
    fulfillmentPct: number;
    commissionPct: number;
    collectionMode: string;
  };
}) {
  // Determine color based on operating margin
  const getColorScheme = () => {
    const marginPct = operatingMarginPct * 100;
    if (marginPct < 0) return {
      border: 'border-l-red-500',
      text: 'text-red-600',
      bg: 'bg-red-500'
    };
    if (marginPct < 30) return {
      border: 'border-l-red-500',
      text: 'text-red-600',
      bg: 'bg-red-500'
    };
    if (marginPct < 40) return {
      border: 'border-l-yellow-500',
      text: 'text-yellow-600',
      bg: 'bg-yellow-500'
    };
    return {
      border: 'border-l-emerald-500',
      text: 'text-emerald-600',
      bg: 'bg-emerald-500'
    };
  };
  const colorScheme = getColorScheme();
  // Generate explanation content for tooltip with enhanced diagnostics
  const getExplanationContent = () => {
    const marginPct = operatingMarginPct * 100;
    const ltvCacRatio = inputs.ltvTotal / metrics.costPerClientFromBooked;
    // Industry benchmarks
    const BENCHMARK_CPBC_LOW = 100;
    const BENCHMARK_CPBC_HIGH = 200;
    const BENCHMARK_LTV_COLLECTION_MONTHS = 3;
    const BENCHMARK_MARGIN_HEALTHY = 30;
    const BENCHMARK_LTV_CAC_RATIO = 3;
    // Collection speed analysis
    const collectionSpeedIssue = () => {
      if (inputs.collectionMode === 'upfront') return false;
      if (inputs.collectionMode === 'equal' && inputs.ltvMonths > BENCHMARK_LTV_COLLECTION_MONTHS) {
        return true;
      }
      if (inputs.collectionMode === 'split' && (inputs.upfrontPct < 0.5 || inputs.remainderMonths > BENCHMARK_LTV_COLLECTION_MONTHS)) {
        return true;
      }
      return false;
    };
    // Cost per booked call analysis
    const cpbcIssue = inputs.costPerBookedCall > BENCHMARK_CPBC_HIGH;
    // Conversion rate analysis
    const conversionRate = inputs.showRate * inputs.closeRate;
    const conversionIssue = conversionRate < 0.15;
    return <div className="space-y-3 max-w-xs" data-id="element-509">
        <div data-id="element-510">
          <h4 className="font-medium text-white" data-id="element-511">Business Model Analysis</h4>
          <p className="mt-1 text-gray-200" data-id="element-512">
            {marginPct < 0 ? 'Your business is operating at a loss. Review your costs and pricing structure immediately.' : marginPct < BENCHMARK_MARGIN_HEALTHY ? `Your profit margin (${marginPct.toFixed(1)}%) is below the recommended ${BENCHMARK_MARGIN_HEALTHY}% for sustainable growth.` : `Your business model appears healthy with a strong ${marginPct.toFixed(1)}% profit margin.`}
          </p>
        </div>
        <div data-id="element-513">
          <h4 className="font-medium text-white" data-id="element-514">LTV:CAC Ratio</h4>
          <p className="mt-1 text-gray-200" data-id="element-515">
            Your ratio is {ltvCacRatio.toFixed(1)}:1.
            {ltvCacRatio < BENCHMARK_LTV_CAC_RATIO ? ` This is below the recommended ${BENCHMARK_LTV_CAC_RATIO}:1 minimum for sustainable growth. Consider increasing your pricing or reducing acquisition costs.` : ` This is a healthy ratio for sustainable growth.`}
          </p>
        </div>
        {collectionSpeedIssue() && <div data-id="element-516">
            <h4 className="font-medium text-white" data-id="element-517">Cash Collection Speed</h4>
            <p className="mt-1 text-gray-200" data-id="element-518">
              {inputs.collectionMode === 'equal' ? `You're collecting LTV over ${inputs.ltvMonths} months, which is slower than the ideal ${BENCHMARK_LTV_COLLECTION_MONTHS} months. This delays your return on ad spend.` : `Your upfront payment (${(inputs.upfrontPct * 100).toFixed(0)}%) and remainder collection (${inputs.remainderMonths} months) is slower than optimal. Consider increasing upfront percentage or reducing payment terms.`}
            </p>
          </div>}
        {cpbcIssue && <div data-id="element-519">
            <h4 className="font-medium text-white" data-id="element-520">
              Customer Acquisition Cost
            </h4>
            <p className="mt-1 text-gray-200" data-id="element-521">
              Your cost per booked call (${inputs.costPerBookedCall}) is higher
              than the industry benchmark (${BENCHMARK_CPBC_LOW}-$
              {BENCHMARK_CPBC_HIGH}). Optimize your ad targeting or messaging to
              reduce this cost.
            </p>
          </div>}
        {conversionIssue && <div data-id="element-522">
            <h4 className="font-medium text-white" data-id="element-523">Conversion Rates</h4>
            <p className="mt-1 text-gray-200" data-id="element-524">
              Your funnel conversion rate (${(conversionRate * 100).toFixed(1)}
              %) is below optimal levels. Focus on improving your show rate ($
              {(inputs.showRate * 100).toFixed(0)}%) and close rate ($
              {(inputs.closeRate * 100).toFixed(0)}%).
            </p>
          </div>}
        {inputs.fulfillmentPct > 0.4 && <div data-id="element-525">
            <h4 className="font-medium text-white" data-id="element-526">Fulfillment Costs</h4>
            <p className="mt-1 text-gray-200" data-id="element-527">
              Your fulfillment costs at{' '}
              {(inputs.fulfillmentPct * 100).toFixed(0)}% of revenue are high.
              Consider optimizing delivery processes or increasing prices to
              improve margins.
            </p>
          </div>}
      </div>;
  };
  return <Card className={`border-l-4 ${colorScheme.border} border-gray-200 hover:shadow-md transition-shadow duration-200`} data-id="element-528">
      <CardContent className="p-5 space-y-2" data-id="element-529">
        <div className={`text-sm font-medium ${colorScheme.text} uppercase tracking-wide flex items-center`} data-id="element-530">
          <span data-id="element-531">Operating Profit</span>
          <Tooltip data-id="element-532">
            <TooltipTrigger className="group" data-id="element-533">
              <span className={`w-5 h-5 ml-2 flex items-center justify-center rounded-full ${colorScheme.bg} bg-opacity-20 text-xs ${colorScheme.text} hover:bg-opacity-30 transition-colors`} data-id="element-534">
                ?
              </span>
              <TooltipContent className="w-80" data-id="element-535">
                {getExplanationContent()}
              </TooltipContent>
            </TooltipTrigger>
          </Tooltip>
        </div>
        <div className={`text-3xl font-bold ${operatingProfitMonthly < 0 ? 'text-red-600' : 'text-gray-800'}`} data-id="element-536">
          {fmtCurrency(operatingProfitMonthly)}
        </div>
        <div className="text-sm text-gray-500 flex items-center justify-between" data-id="element-537">
          <span data-id="element-538">Monthly</span>
          <span className={`font-medium ${colorScheme.text}`} data-id="element-539">
            {(operatingMarginPct * 100).toFixed(1)}% margin
          </span>
        </div>
        <div className="text-xs text-gray-400 pt-2 border-t border-gray-100 mt-2" data-id="element-540">
          After ad spend, commissions, and fulfillment costs
        </div>
      </CardContent>
    </Card>;
}
// New Scalability Card component
function ScalabilityCard({
  score,
  assessment,
  ltvCacRatio,
  operatingMarginPct,
  cashCollectionSpeed
}: {
  score: number;
  assessment: string;
  ltvCacRatio: number;
  operatingMarginPct: number;
  cashCollectionSpeed: number;
}) {
  // Determine color based on scalability score
  const getColorScheme = () => {
    if (score < 40) return {
      border: 'border-l-red-500',
      text: 'text-red-600',
      bg: 'bg-red-500'
    };
    if (score < 60) return {
      border: 'border-l-yellow-500',
      text: 'text-yellow-600',
      bg: 'bg-yellow-500'
    };
    if (score < 80) return {
      border: 'border-l-blue-500',
      text: 'text-blue-600',
      bg: 'bg-blue-500'
    };
    return {
      border: 'border-l-emerald-500',
      text: 'text-emerald-600',
      bg: 'bg-emerald-500'
    };
  };
  const colorScheme = getColorScheme();
  // Generate explanation content for tooltip
  const getExplanationContent = () => {
    return <div className="space-y-3 max-w-xs" data-id="element-541">
        <div data-id="element-542">
          <h4 className="font-medium text-white" data-id="element-543">
            Scalability Score: {score}/100
          </h4>
          <p className="mt-1 text-gray-200" data-id="element-544">
            Based on your business model's operating margin, LTV:CAC ratio, and
            cash collection speed.
          </p>
        </div>
        <div data-id="element-545">
          <h4 className="font-medium text-white" data-id="element-546">Key Factors</h4>
          <ul className="mt-1 text-gray-200 space-y-2" data-id="element-547">
            <li className="flex justify-between" data-id="element-548">
              <span data-id="element-549">Operating Margin:</span>
              <span data-id="element-550">{(operatingMarginPct * 100).toFixed(1)}%</span>
            </li>
            <li className="flex justify-between" data-id="element-551">
              <span data-id="element-552">LTV:CAC Ratio:</span>
              <span data-id="element-553">{ltvCacRatio.toFixed(1)}:1</span>
            </li>
            <li className="flex justify-between" data-id="element-554">
              <span data-id="element-555">Cash Collection (months):</span>
              <span data-id="element-556">{cashCollectionSpeed}</span>
            </li>
          </ul>
        </div>
        <div data-id="element-557">
          <h4 className="font-medium text-white" data-id="element-558">Assessment</h4>
          <p className="mt-1 text-gray-200" data-id="element-559">
            {score >= 80 ? 'Your business model is highly scalable with strong unit economics and efficient cash collection.' : score >= 60 ? 'Your business can scale but may require optimization in certain areas to maximize growth potential.' : score >= 40 ? 'Your business has moderate scalability challenges that should be addressed before aggressive scaling.' : 'Your current business model faces significant scalability challenges. Consider revising your pricing, acquisition, or collection strategies.'}
          </p>
        </div>
      </div>;
  };
  return <Card className={`border-l-4 ${colorScheme.border} border-gray-200 hover:shadow-md transition-shadow duration-200`} data-id="element-560">
      <CardContent className="p-5 space-y-2" data-id="element-561">
        <div className={`text-sm font-medium ${colorScheme.text} uppercase tracking-wide flex items-center`} data-id="element-562">
          <span data-id="element-563">Scalability Viability</span>
          <Tooltip data-id="element-564">
            <TooltipTrigger className="group" data-id="element-565">
              <span className={`w-5 h-5 ml-2 flex items-center justify-center rounded-full ${colorScheme.bg} bg-opacity-20 text-xs ${colorScheme.text} hover:bg-opacity-30 transition-colors`} data-id="element-566">
                ?
              </span>
              <TooltipContent className="w-80" data-id="element-567">
                {getExplanationContent()}
              </TooltipContent>
            </TooltipTrigger>
          </Tooltip>
        </div>
        <div className={`text-3xl font-bold ${colorScheme.text}`} data-id="element-568">{score}%</div>
        <div className="text-sm text-gray-500 flex items-center justify-between" data-id="element-569">
          <span data-id="element-570">Score</span>
          <span className={`font-medium ${colorScheme.text}`} data-id="element-571">
            {assessment}
          </span>
        </div>
        <div className="text-xs text-gray-400 pt-2 border-t border-gray-100 mt-2" data-id="element-572">
          Based on margin, LTV:CAC, and cash collection speed
        </div>
      </CardContent>
    </Card>;
}
// New CAC Card component
function CACCard({
  cac,
  fullyLoadedCAC,
  ltvCacRatio,
  fullyLoadedLtvCacRatio,
  ltv,
  showRate,
  closeRate,
  costPerBookedCall,
  costPerShow,
  salesAndMarketingCosts,
  commissionPerClient
}: {
  cac: number;
  fullyLoadedCAC: number;
  ltvCacRatio: number;
  fullyLoadedLtvCacRatio: number;
  ltv: number;
  showRate: number;
  closeRate: number;
  costPerBookedCall: number;
  costPerShow: number;
  salesAndMarketingCosts: number;
  commissionPerClient: number;
}) {
  // Determine color based on LTV:CAC ratio (using fully loaded CAC for color)
  const getColorScheme = () => {
    if (fullyLoadedLtvCacRatio < 2) return {
      border: 'border-l-red-500',
      text: 'text-red-600',
      bg: 'bg-red-500'
    };
    if (fullyLoadedLtvCacRatio < 3) return {
      border: 'border-l-yellow-500',
      text: 'text-yellow-600',
      bg: 'bg-yellow-500'
    };
    return {
      border: 'border-l-blue-500',
      text: 'text-blue-600',
      bg: 'bg-blue-500'
    };
  };
  const colorScheme = getColorScheme();
  // Calculate payback period (months) - simplified calculation
  const paybackMonths = ltv > 0 ? Math.ceil(fullyLoadedCAC / (ltv / 6)) : 0; // Assuming 6 months to collect full LTV
  // Generate explanation content for tooltip
  const getExplanationContent = () => {
    return <div className="space-y-3 max-w-xs" data-id="element-573">
        <div data-id="element-574">
          <h4 className="font-medium text-white" data-id="element-575">Customer Acquisition Cost</h4>
          <p className="mt-1 text-gray-200" data-id="element-576">
            The average cost to acquire one paying customer through your sales
            funnel.
          </p>
        </div>
        <div data-id="element-577">
          <h4 className="font-medium text-white" data-id="element-578">Basic vs Fully Loaded CAC</h4>
          <p className="mt-1 text-gray-200" data-id="element-579">
            Basic CAC only includes direct ad spend costs. Fully Loaded CAC adds
            all sales and marketing expenses plus commissions.
          </p>
          <ul className="mt-1 text-gray-200 space-y-1" data-id="element-580">
            <li className="flex justify-between" data-id="element-581">
              <span data-id="element-582">Basic CAC:</span>
              <span data-id="element-583">{fmtCurrency(cac)}</span>
            </li>
            <li className="flex justify-between" data-id="element-584">
              <span data-id="element-585">S&M Costs Per Client:</span>
              <span data-id="element-586">
                {fmtCurrency(salesAndMarketingCosts > 0 ? fullyLoadedCAC - cac - commissionPerClient : 0)}
              </span>
            </li>
            <li className="flex justify-between" data-id="element-587">
              <span data-id="element-588">Commission Per Client:</span>
              <span data-id="element-589">{fmtCurrency(commissionPerClient)}</span>
            </li>
          </ul>
        </div>
        <div data-id="element-590">
          <h4 className="font-medium text-white" data-id="element-591">Funnel Metrics</h4>
          <ul className="mt-1 text-gray-200 space-y-1" data-id="element-592">
            <li className="flex justify-between" data-id="element-593">
              <span data-id="element-594">Show Rate:</span>
              <span data-id="element-595">{(showRate * 100).toFixed(1)}%</span>
            </li>
            <li className="flex justify-between" data-id="element-596">
              <span data-id="element-597">Close Rate:</span>
              <span data-id="element-598">{(closeRate * 100).toFixed(1)}%</span>
            </li>
            <li className="flex justify-between" data-id="element-599">
              <span data-id="element-600">Cost/Booked Call:</span>
              <span data-id="element-601">{fmtCurrency(costPerBookedCall)}</span>
            </li>
            <li className="flex justify-between" data-id="element-602">
              <span data-id="element-603">Cost/Show:</span>
              <span data-id="element-604">{fmtCurrency(costPerShow)}</span>
            </li>
          </ul>
        </div>
        <div data-id="element-605">
          <h4 className="font-medium text-white" data-id="element-606">LTV:CAC Ratio</h4>
          <p className="mt-1 text-gray-200" data-id="element-607">
            {fullyLoadedLtvCacRatio < 3 ? 'Your LTV:CAC ratio should be at least 3:1 for a healthy business model. Consider increasing your pricing or optimizing your funnel.' : 'Your LTV:CAC ratio is healthy! This indicates your customer acquisition strategy is sustainable.'}
          </p>
        </div>
        <div data-id="element-608">
          <h4 className="font-medium text-white" data-id="element-609">Payback Period</h4>
          <p className="mt-1 text-gray-200" data-id="element-610">
            It takes approximately {paybackMonths} months to recover your
            customer acquisition cost.
            {paybackMonths > 6 ? ' This is longer than ideal. Consider improving your pricing or collection terms.' : ' This is a reasonable payback period for most businesses.'}
          </p>
        </div>
      </div>;
  };
  return <Card className={`border-l-4 ${colorScheme.border} border-gray-200 hover:shadow-md transition-shadow duration-200`} data-id="element-611">
      <CardContent className="p-5 space-y-2" data-id="element-612">
        <div className={`text-sm font-medium ${colorScheme.text} uppercase tracking-wide flex items-center`} data-id="element-613">
          <span data-id="element-614">Customer Acquisition</span>
          <Tooltip data-id="element-615">
            <TooltipTrigger className="group" data-id="element-616">
              <span className={`w-5 h-5 ml-2 flex items-center justify-center rounded-full ${colorScheme.bg} bg-opacity-20 text-xs ${colorScheme.text} hover:bg-opacity-30 transition-colors`} data-id="element-617">
                ?
              </span>
              <TooltipContent className="w-80" data-id="element-618">
                {getExplanationContent()}
              </TooltipContent>
            </TooltipTrigger>
          </Tooltip>
        </div>
        <div className="text-3xl font-bold text-gray-800" data-id="element-619">
          {fmtCurrency(fullyLoadedCAC)}
        </div>
        <div className="text-sm text-gray-500 flex items-center justify-between" data-id="element-620">
          <span data-id="element-621">Fully Loaded CAC</span>
          <span className={`font-medium ${colorScheme.text}`} data-id="element-622">
            {fullyLoadedLtvCacRatio.toFixed(1)}x LTV:CAC
          </span>
        </div>
        <div className="mt-1 text-xs text-gray-500" data-id="element-623">
          <div className="flex justify-between items-center" data-id="element-624">
            <span data-id="element-625">Basic CAC:</span>
            <span className="font-medium" data-id="element-626">{fmtCurrency(cac)}</span>
          </div>
          <div className="flex justify-between items-center mt-1" data-id="element-627">
            <span data-id="element-628">+ Commission:</span>
            <span className="font-medium" data-id="element-629">
              {fmtCurrency(commissionPerClient)}
            </span>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-630">
          <div className="grid grid-cols-2 gap-2" data-id="element-631">
            <div className="text-xs" data-id="element-632">
              <span className="text-gray-500" data-id="element-633">Conversion:</span>
              <div className="font-medium text-gray-800 mt-1" data-id="element-634">
                {(showRate * closeRate * 100).toFixed(1)}%
              </div>
            </div>
            <div className="text-xs" data-id="element-635">
              <span className="text-gray-500" data-id="element-636">Payback Period:</span>
              <div className="font-medium text-gray-800 mt-1" data-id="element-637">
                {paybackMonths} months
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>;
}
// Modify the KPI cards to include tooltips
function KpiCard({
  title,
  value,
  subtitle,
  footnote,
  borderColor,
  textColor,
  tooltipContent
}: {
  title: string;
  value: string;
  subtitle?: React.ReactNode;
  footnote?: string;
  borderColor: string;
  textColor: string;
  tooltipContent?: React.ReactNode;
}) {
  return <Card className={`border-l-4 ${borderColor} border-gray-200 hover:shadow-md transition-shadow duration-200`} data-id="element-638">
      <CardContent className="p-5 space-y-2" data-id="element-639">
        <div className={`text-sm font-medium ${textColor} uppercase tracking-wide flex items-center`} data-id="element-640">
          <span data-id="element-641">{title}</span>
          {tooltipContent && <Tooltip data-id="element-642">
              <TooltipTrigger className="group" data-id="element-643">
                <span className="w-4 h-4 ml-1.5 flex items-center justify-center rounded-full bg-gray-200 text-gray-600 text-[10px] hover:bg-gray-300 transition-colors" data-id="element-644">
                  ?
                </span>
                <TooltipContent data-id="element-645">{tooltipContent}</TooltipContent>
              </TooltipTrigger>
            </Tooltip>}
        </div>
        <div className="text-3xl font-bold text-gray-800" data-id="element-646">{value}</div>
        {subtitle && <div className="text-sm text-gray-500 flex items-center justify-between" data-id="element-647">
            {subtitle}
          </div>}
        {footnote && <div className="text-xs text-gray-400 pt-2 border-t border-gray-100 mt-2" data-id="element-648">
            {footnote}
          </div>}
      </CardContent>
    </Card>;
}
// Add this new component for metric comparisons
function ComparisonCard({
  title,
  currentValue,
  benchmarkValue,
  format,
  improvement,
  improvementLabel,
  tooltipContent,
  inversed = false
}: {
  title: string;
  currentValue: number;
  benchmarkValue: number;
  format: 'percentage' | 'currency';
  improvement: string;
  improvementLabel: string;
  tooltipContent?: React.ReactNode;
  inversed?: boolean;
}) {
  const formatValue = (value: number) => {
    if (format === 'percentage') {
      return `${(value * 100).toFixed(0)}%`;
    } else {
      return fmtCurrency(value);
    }
  };
  const isCurrentBetter = inversed ? currentValue <= benchmarkValue : currentValue >= benchmarkValue;
  const percentageDiff = inversed ? benchmarkValue < currentValue ? (1 - benchmarkValue / currentValue) * 100 : 0 : benchmarkValue > currentValue ? (1 - currentValue / benchmarkValue) * 100 : 0;
  return <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-649">
      <div className="flex items-center justify-between bg-gray-50 px-3 py-2 border-b border-gray-200" data-id="element-650">
        <div className="flex items-center" data-id="element-651">
          <span className="font-medium text-gray-700" data-id="element-652">{title}</span>
          {tooltipContent && <Tooltip data-id="element-653">
              <TooltipTrigger className="group" data-id="element-654">
                <span className="w-4 h-4 ml-1.5 flex items-center justify-center rounded-full bg-gray-200 text-gray-600 text-[10px] hover:bg-gray-300 transition-colors" data-id="element-655">
                  ?
                </span>
                <TooltipContent data-id="element-656">{tooltipContent}</TooltipContent>
              </TooltipTrigger>
            </Tooltip>}
        </div>
      </div>
      <div className="px-3 py-3" data-id="element-657">
        <div className="flex justify-between mb-2" data-id="element-658">
          <span className="text-sm text-gray-500" data-id="element-659">Current</span>
          <span className="text-sm text-gray-500" data-id="element-660">Benchmark</span>
        </div>
        <div className="flex justify-between mb-3" data-id="element-661">
          <span className={`text-lg font-bold ${isCurrentBetter ? 'text-green-600' : 'text-red-600'}`} data-id="element-662">
            {formatValue(currentValue)}
          </span>
          <span className="text-lg font-bold text-blue-600" data-id="element-663">
            {formatValue(benchmarkValue)}
          </span>
        </div>
        {!isCurrentBetter && <div className="mt-2 pt-2 border-t border-gray-100" data-id="element-664">
            <div className="text-sm text-gray-600 mb-1" data-id="element-665">
              <span className="font-medium text-red-600" data-id="element-666">
                Room for improvement:
              </span>
            </div>
            <div className="flex items-end justify-between" data-id="element-667">
              <span className="text-red-600 font-medium" data-id="element-668">
                {percentageDiff > 0 ? `${percentageDiff.toFixed(0)}%` : 'None'}
              </span>
              <div className="text-right" data-id="element-669">
                <span className="block text-emerald-600 font-bold" data-id="element-670">
                  {improvement}
                </span>
                <span className="block text-xs text-gray-500" data-id="element-671">
                  {improvementLabel}
                </span>
              </div>
            </div>
          </div>}
        {isCurrentBetter && <div className="mt-2 pt-2 border-t border-gray-100" data-id="element-672">
            <div className="text-sm text-gray-600 mb-1" data-id="element-673">Status:</div>
            <div className="text-green-600 font-medium" data-id="element-674">
              Already meeting or exceeding benchmark!
            </div>
          </div>}
      </div>
    </div>;
}